import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { reparentNode } from '../utils/nodeOperations';
import { ArrowLeft, Download, Copy, Play, Plus, FileText, ZoomIn, ZoomOut, Maximize2, ChevronDown, ChevronRight, Minus, Loader2, Palette, Link, ExternalLink, X, Check, Undo2, Save, Upload, Trash2, BookOpen } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PageHeader from './PageHeader';
import NodeEditModal from './NodeEditModal';
import { parseTweeToGraph, graphToTwee, generateNodeName, resetNodeCounter } from '../utils/tweeConverter';
import { getNodeStyle, getConnectionStyle, loadStyleConfig, saveStyleConfig, defaultStyleConfig } from '../utils/nodeStyles';
import StyleFormattingPanel from './StyleFormattingPanel';
import NodeFormatEditor, { useNodeFormat, DEFAULT_NODE_FORMAT } from './NodeFormatEditor';
import { API_BASE_URL } from '../config/api';
import { downloadProject, importProjectFromFile, serializeProject } from '../utils/projectSerializer';
import { copyNoJsHtml } from '../utils/exportNoJs';
import { copyWordPressHtml } from '../utils/exportWordPress';
import { generatePathNavHtml } from '../utils/pathnav-engine';
import { copyWordPressStoryHtml } from '../utils/exportWordPressStory';

// Node dimensions for hierarchical layout
const NODE_WIDTH = 260;
const NODE_HEIGHT_FULL = 100;  // Height with description
const NODE_HEIGHT_COMPACT = 40;  // Height without description (title bar only, matching rounded corners)
const HORIZONTAL_GAP = 60;
const VERTICAL_GAP = 20;

// Compact Focus Mode dimensions (for mobile-friendly navigation)
const NODE_WIDTH_COMPACT_FOCUS = 160;
const NODE_HEIGHT_COMPACT_FOCUS = 36;
const HORIZONTAL_GAP_COMPACT_FOCUS = 20;  // Minimal gap - nodes almost touch
const VERTICAL_GAP_COMPACT_FOCUS = 6;     // Tight vertical stacking

// Minimum and maximum node width when using dynamic width calculation
const MIN_NODE_WIDTH = 80;
const MIN_NODE_WIDTH_COMPACT = 60;
const MAX_NODE_WIDTH_ABSOLUTE = 500; // Absolute maximum width for any node
const TOGGLE_PREFERENCES_KEY = 'visual_editor_toggle_preferences';

const getTogglePreferences = () => {
  try { return JSON.parse(localStorage.getItem(TOGGLE_PREFERENCES_KEY) || '{}'); }
  catch { return {}; }
};

// Helper function to measure text width using canvas
const measureTextWidth = (text, fontSize = 14, fontWeight = '600') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
  return context.measureText(text).width;
};

// Calculate the width needed for a node based on its text content
// Returns the width required to fit the text plus internal padding
const calculateNodeWidthForText = (text, isCompact = false) => {
  const textWidth = measureTextWidth(text, 14, '600');
  // Add padding: 12px left margin + 80px for icons/buttons on right (chevron + collapse + URL icons + buffer)
  // Using 80px to account for worst-case scenario with all icons visible plus safety margin
  const baseWidth = textWidth + 12 + 80;
  const minWidth = isCompact ? MIN_NODE_WIDTH_COMPACT : MIN_NODE_WIDTH;
  return Math.max(baseWidth, minWidth);
};

// Zoom presets
const ZOOM_PRESETS = [5, 10, 25, 50, 75, 100, 125, 150, 200, 250, 300];
const ZOOM_STEP = 30;
const MIN_ZOOM = 5;
const MAX_ZOOM = 300;

const VisualEditor = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const togglePreferences = useMemo(getTogglePreferences, []);
  
  const [tweeInput, setTweeInput] = useState('');
  const [graph, setGraph] = useState({ nodes: [], edges: [], storyTitle: 'Interactive Story' });
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [preloadSource, setPreloadSource] = useState(null);
  
  // Undo history - stores last 20 graph states
  const [undoHistory, setUndoHistory] = useState([]);
  const MAX_UNDO_HISTORY = 20;
  
  // Save current state to undo history before changes
  const saveToUndoHistory = useCallback((currentGraph) => {
    setUndoHistory(prev => {
      const newHistory = [...prev, JSON.parse(JSON.stringify(currentGraph))];
      // Keep only last 20 states
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, []);
  
  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) {
      toast({ title: 'Nothing to undo', description: 'No previous states available' });
      return;
    }
    
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setGraph(previousState);
    toast({ title: 'Undone', description: 'Reverted to previous state' });
  }, [undoHistory]);
  
  // Keyboard shortcut for undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo]);
  
  // Collapsed nodes state - stores node IDs that are collapsed
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  
  // Zoom and pan state - use smaller zoom on mobile
  const [zoom, setZoom] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 60; // Smaller zoom for mobile
    }
    return 100;
  });
  const [panOffset, setPanOffset] = useState({ x: 20, y: 20 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [startOffset, setStartOffset] = useState({ x: 0, y: 0 });
  const [showZoomDropdown, setShowZoomDropdown] = useState(false);
  
  // Pinch zoom state
  const [lastPinchDistance, setLastPinchDistance] = useState(null);
  
  // Layout state - calculated via backend API for IP protection
  const [layoutedNodes, setLayoutedNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [nodeChildrenMap, setNodeChildrenMap] = useState(new Map());
  const [isCalculatingLayout, setIsCalculatingLayout] = useState(false);
  
  // Layout options state
  const [layoutDirection, setLayoutDirection] = useState('right');
  const [nodeAlignment, setNodeAlignment] = useState('center');
  const [showDescription, setShowDescription] = useState(() => Boolean(togglePreferences.showDescription));
  const [descriptionCharLimit, setDescriptionCharLimit] = useState(80);
  const [showBackLinks, setShowBackLinks] = useState(() => Boolean(togglePreferences.showBackLinks));
  
  // PathNav node level filters
  const [hideH1Nodes, setHideH1Nodes] = useState(() => Boolean(togglePreferences.hideH1Nodes));
  const [hideH2Nodes, setHideH2Nodes] = useState(() => Boolean(togglePreferences.hideH2Nodes));
  
  // Style configuration
  const [styleConfig, setStyleConfig] = useState(defaultStyleConfig);
  const [showStylePanel, setShowStylePanel] = useState(false);
  
  // Node format configuration (for node style types: standard, light, unbounded)
  const [nodeFormat, setNodeFormat] = useNodeFormat();
  const [showNodeFormatEditor, setShowNodeFormatEditor] = useState(false);
  
  // Per-node description expand state
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  
  // Focus node state
  const [focusNodeId, setFocusNodeId] = useState(null);
  const [focusNodeViewEnabled, setFocusNodeViewEnabled] = useState(() => Boolean(togglePreferences.focusNodeViewEnabled));
  const [focusLayerDepth, setFocusLayerDepth] = useState(1);
  const [focusTravelCenter, setFocusTravelCenter] = useState(false);
  const [autoImportPending, setAutoImportPending] = useState(false);
  const [centerOnStartPending, setCenterOnStartPending] = useState(false);
  
  // Dynamic Node View state (Story Map Mode)
  const [dynamicViewEnabled, setDynamicViewEnabled] = useState(() => Boolean(togglePreferences.dynamicViewEnabled));
  const [dynamicViewSize, setDynamicViewSize] = useState('standard'); // 'standard' or 'compact'
  
  // PathNav Sitemap Mode state
  const [pathNavSitemapMode, setPathNavSitemapMode] = useState(() => Boolean(togglePreferences.pathNavSitemapMode));
  const [pathNavFocusColor, setPathNavFocusColor] = useState('#f97316');
  const [pathNavStartMinimized, setPathNavStartMinimized] = useState(true);
  
  // Node Width Padding state
  const [nodeWidthPaddingEnabled, setNodeWidthPaddingEnabled] = useState(() => Boolean(togglePreferences.nodeWidthPaddingEnabled));
  const [nodeWidthPaddingPercent, setNodeWidthPaddingPercent] = useState('50'); // '0', '10', '30', '50', '80', '100'
  const [calculatedNodeWidth, setCalculatedNodeWidth] = useState(NODE_WIDTH); // Actual width used for rendering
  
  // Drag-and-drop reparenting state
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedNodeId: null,
    dragStartPos: null,
    currentPos: null,
    dropTargetId: null,
    holdProgress: 0
  });
  const dragTimerRef = useRef(null);
  const holdProgressRef = useRef(null);
  const DRAG_HOLD_DELAY = 500; // ms to hold before drag activates
  
  // Apply editor configuration from imported Twee file
  // Must be declared before auto-import useEffect that uses it
  const applyEditorConfig = useCallback((config) => {
    if (!config) return;
    
    console.log('[VisualEditor] Applying editorConfig:', config);
    
    // PathNav Sitemap Mode settings
    if (config.pathNavSitemapEnabled !== undefined) {
      setPathNavSitemapMode(config.pathNavSitemapEnabled);
    }
    if (config.pathNavFocusColor) {
      setPathNavFocusColor(config.pathNavFocusColor);
    }
    if (config.pathNavStartMinimized !== undefined) {
      setPathNavStartMinimized(config.pathNavStartMinimized);
    }
    
    // Node Width Padding settings
    if (config.nodeWidthPaddingEnabled !== undefined) {
      setNodeWidthPaddingEnabled(config.nodeWidthPaddingEnabled);
    }
    if (config.nodeWidthPaddingPercent) {
      setNodeWidthPaddingPercent(config.nodeWidthPaddingPercent);
    }
    
    // Focus View settings
    if (config.focusNodeViewEnabled !== undefined) {
      setFocusNodeViewEnabled(config.focusNodeViewEnabled);
    }
    if (config.focusLayerDepth !== undefined) {
      setFocusLayerDepth(config.focusLayerDepth);
    }
    if (config.focusTravelCenter !== undefined) {
      setFocusTravelCenter(config.focusTravelCenter);
    }
    
    // Dynamic View settings
    if (config.dynamicViewEnabled !== undefined) {
      setDynamicViewEnabled(config.dynamicViewEnabled);
    }
    if (config.dynamicViewSize) {
      setDynamicViewSize(config.dynamicViewSize);
    }
    
    // Layout settings
    if (config.layoutDirection) {
      setLayoutDirection(config.layoutDirection);
    }
    if (config.nodeAlignment) {
      setNodeAlignment(config.nodeAlignment);
    }
    if (config.showDescription !== undefined) {
      setShowDescription(config.showDescription);
    }
    if (config.showBackLinks !== undefined) {
      setShowBackLinks(config.showBackLinks);
    }
    
    // PathNav level filters
    if (config.hideH1Nodes !== undefined) {
      setHideH1Nodes(config.hideH1Nodes);
    }
    if (config.hideH2Nodes !== undefined) {
      setHideH2Nodes(config.hideH2Nodes);
    }
    
    toast({ title: 'Settings Restored', description: 'Editor configuration loaded from file.' });
  }, []);

  // Build current editorConfig object for saving
  const buildEditorConfig = useCallback(() => {
    return {
      // PathNav Sitemap Mode settings
      pathNavSitemapEnabled: pathNavSitemapMode,
      pathNavFocusColor: pathNavFocusColor,
      pathNavStartMinimized: pathNavStartMinimized,
      
      // Node Width Padding settings
      nodeWidthPaddingEnabled: nodeWidthPaddingEnabled,
      nodeWidthPaddingPercent: nodeWidthPaddingPercent,
      
      // Focus View settings
      focusNodeViewEnabled: focusNodeViewEnabled,
      focusLayerDepth: focusLayerDepth,
      focusTravelCenter: focusTravelCenter,
      
      // Dynamic View settings
      dynamicViewEnabled: dynamicViewEnabled,
      dynamicViewSize: dynamicViewSize,
      
      // Layout settings
      layoutDirection: layoutDirection,
      nodeAlignment: nodeAlignment,
      showDescription: showDescription,
      showBackLinks: showBackLinks,
      
      // PathNav level filters
      hideH1Nodes: hideH1Nodes,
      hideH2Nodes: hideH2Nodes
    };
  }, [
    pathNavSitemapMode, pathNavFocusColor, pathNavStartMinimized,
    nodeWidthPaddingEnabled, nodeWidthPaddingPercent,
    focusNodeViewEnabled, focusLayerDepth, focusTravelCenter,
    dynamicViewEnabled, dynamicViewSize,
    layoutDirection, nodeAlignment, showDescription, showBackLinks,
    hideH1Nodes, hideH2Nodes
  ]);
  
  useEffect(() => {
    if (!showCanvas) {
      resetNodeCounter();
    }
  }, [showCanvas]);

  useEffect(() => {
    localStorage.setItem(TOGGLE_PREFERENCES_KEY, JSON.stringify({ showDescription, showBackLinks, hideH1Nodes, hideH2Nodes, focusNodeViewEnabled, dynamicViewEnabled, pathNavSitemapMode, nodeWidthPaddingEnabled }));
  }, [showDescription, showBackLinks, hideH1Nodes, hideH2Nodes, focusNodeViewEnabled, dynamicViewEnabled, pathNavSitemapMode, nodeWidthPaddingEnabled]);
  
  useEffect(() => {
    // Load style config from localStorage
    const loadedStyleConfig = loadStyleConfig();
    setStyleConfig(loadedStyleConfig);
    
    // Check for starter template from URL param
    const urlParams = new URLSearchParams(window.location.search);
    const showStarter = urlParams.get('starter') === 'true';
    
    if (showStarter) {
      // Clear URL param
      window.history.replaceState({}, '', window.location.pathname);
      
      // Create starter template with instruction node
      resetNodeCounter();
      const startNode = {
        id: 'Start',
        name: 'Start',
        body: 'Your story begins here.\n\n[[Double Tap or Click|instruction]]',
        x: 0,
        y: 200
      };
      const instructionNode = {
        id: 'instruction',
        name: 'Double Tap or Click',
        body: 'Double tap or click on any node to add or edit content.\n\nTry it now!',
        x: 350,
        y: 200
      };
      setGraph({ 
        nodes: [startNode, instructionNode], 
        edges: [{ from: 'Start', to: 'instruction' }], 
        storyTitle: 'My Interactive Story' 
      });
      setShowCanvas(true);
      setZoom(70);
      setCenterOnStartPending(true);
      setCollapsedNodes(new Set());
      toast({ title: 'Welcome to Visual Editor!', description: 'Double-click any node to edit.' });
      return;
    }
    
    // PRIORITY 1: Check for fresh imports from Sitemap Importer (highest priority)
    const importedTwee = sessionStorage.getItem('imported_twee');
    const autoImport = sessionStorage.getItem('imported_twee_auto');
    if (importedTwee) {
      // Clear any old saved session to ensure fresh import takes priority
      localStorage.removeItem('visual_editor_graph');
      localStorage.removeItem('visual_editor_twee');
      
      setTweeInput(importedTwee);
      sessionStorage.removeItem('imported_twee');
      
      if (autoImport === 'true') {
        sessionStorage.removeItem('imported_twee_auto');
        setAutoImportPending(true);
      } else {
        toast({ title: 'Twee imported', description: 'Click "Import and Generate Map" to visualize your story' });
      }
      return; // Don't restore old session
    }
    
    // PRIORITY 2: Check for planner import
    const plannerImport = localStorage.getItem('visual_editor_import');
    if (plannerImport) {
      try {
        const data = JSON.parse(plannerImport);
        if (data.nodes && Array.isArray(data.nodes)) {
          const nodes = data.nodes.map(node => ({
            id: node.id,
            name: node.name,
            body: node.content || '',
            x: node.x || 100,
            y: node.y || 100
          }));
          const edges = data.edges || [];
          setGraph({ nodes, edges, storyTitle: data.storyTitle || 'Story from Planner' });
          setShowCanvas(true);
          localStorage.removeItem('visual_editor_import');
          toast({ title: 'Story imported from Planner', description: `Loaded ${nodes.length} nodes from Story Planner` });
          return;
        }
      } catch (error) {
        console.error('Error importing from Story Planner:', error);
      }
    }
    
    // PRIORITY 3: Restore saved session (only if no fresh imports)
    const savedGraph = localStorage.getItem('visual_editor_graph');
    const savedTwee = localStorage.getItem('visual_editor_twee');
    
    if (savedGraph && savedTwee) {
      try {
        const parsedGraph = JSON.parse(savedGraph);
        setGraph(parsedGraph);
        setTweeInput(savedTwee);
        setShowCanvas(true);
        toast({ title: 'Session restored', description: 'Your previous work has been restored' });
        return;
      } catch (error) {
        console.error('Error restoring saved state:', error);
      }
    }
    
    if (!savedGraph && savedTwee) {
      setTweeInput(savedTwee);
      setPreloadSource('Previous Session');
    }
  }, []);

  // Auto-import pending Twee code
  useEffect(() => {
    if (autoImportPending && tweeInput.trim()) {
      setAutoImportPending(false);
      try {
        resetNodeCounter();
        const parsedGraph = parseTweeToGraph(tweeInput);
        
        if (parsedGraph.nodes.length > 0) {
          setGraph(parsedGraph);
          setShowCanvas(true);
          setZoom(70);
          setCenterOnStartPending(true); // Will center after layout calculates
          setCollapsedNodes(new Set());
          
          // Apply editor config from imported Twee file if present
          if (parsedGraph.editorConfig) {
            applyEditorConfig(parsedGraph.editorConfig);
          }
          
          toast({ title: 'Sitemap imported!', description: `Loaded ${parsedGraph.nodes.length} pages as a mind map.` });
        }
      } catch (error) {
        console.error('Auto-import error:', error);
        toast({ title: 'Auto-import failed', description: 'Click "Import and Generate Map" to try manually.', variant: 'destructive' });
      }
    }
  }, [autoImportPending, tweeInput, applyEditorConfig]);

  // Toggle collapse state for a node
  const toggleCollapse = useCallback((nodeId) => {
    setCollapsedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Only trigger layout recalc on focusNodeId when focus modes are active
  const layoutFocusNodeId = (focusNodeViewEnabled || dynamicViewEnabled) ? focusNodeId : null;
  
  // Calculate layout via backend API for IP protection
  useEffect(() => {
    const calculateLayoutViaAPI = async () => {
      if (!graph.nodes || graph.nodes.length === 0) {
        setLayoutedNodes([]);
        setEdges([]);
        setCanvasSize({ width: 800, height: 600 });
        setNodeChildrenMap(new Map());
        return;
      }

      setIsCalculatingLayout(true);

      try {
        // Calculate which nodes have expanded descriptions
        // If showDescription is on, all nodes are expanded unless individually collapsed
        // If showDescription is off, only individually expanded nodes are expanded
        const expandedNodeIds = Array.from(expandedDescriptions);
        
        // Use compact dimensions in focus mode or dynamic view mode
        const useCompactFocus = (focusNodeViewEnabled && focusNodeId) || dynamicViewEnabled;
        
        // Calculate dynamic node width based on text content when padding is enabled
        let currentNodeWidth;
        if (nodeWidthPaddingEnabled) {
          // Find the longest node name to calculate the base width
          const maxTextWidth = graph.nodes.reduce((max, node) => {
            const nodeText = node.name || node.id || '';
            const textWidth = calculateNodeWidthForText(nodeText, useCompactFocus);
            return Math.max(max, textWidth);
          }, useCompactFocus ? MIN_NODE_WIDTH_COMPACT : MIN_NODE_WIDTH);
          
          // Apply padding percentage:
          // 0% (Tight) = exactly the text width (no extra padding)
          // 100% = add 100% extra padding (double the space)
          const paddingPercent = parseInt(nodeWidthPaddingPercent);
          const extraPadding = (paddingPercent / 100) * maxTextWidth;
          currentNodeWidth = Math.round(maxTextWidth + extraPadding);
          
          // Ensure minimum widths are respected, cap at absolute maximum
          const minWidth = useCompactFocus ? MIN_NODE_WIDTH_COMPACT : MIN_NODE_WIDTH;
          currentNodeWidth = Math.min(Math.max(currentNodeWidth, minWidth), MAX_NODE_WIDTH_ABSOLUTE);
        } else {
          // Use default fixed widths when padding is disabled
          currentNodeWidth = useCompactFocus ? NODE_WIDTH_COMPACT_FOCUS : NODE_WIDTH;
        }
        
        const currentNodeHeightFull = useCompactFocus ? NODE_HEIGHT_COMPACT_FOCUS : NODE_HEIGHT_FULL;
        const currentNodeHeightCompact = useCompactFocus ? NODE_HEIGHT_COMPACT_FOCUS : NODE_HEIGHT_COMPACT;
        
        // Calculate horizontal gap - smaller when nodes are tighter
        const baseHorizontalGap = useCompactFocus ? HORIZONTAL_GAP_COMPACT_FOCUS : HORIZONTAL_GAP;
        const gapMultiplier = nodeWidthPaddingEnabled 
          ? 0.5 + (parseInt(nodeWidthPaddingPercent) / 100) * 0.5 
          : 1.0;
        const currentHorizontalGap = Math.round(baseHorizontalGap * gapMultiplier);
        const currentVerticalGap = useCompactFocus ? VERTICAL_GAP_COMPACT_FOCUS : VERTICAL_GAP;
        
        // Store calculated width for rendering
        setCalculatedNodeWidth(currentNodeWidth);
        
        // Dynamic View overrides: forces horizontal tree with "below" alignment
        const effectiveDirection = dynamicViewEnabled ? 'right' : layoutDirection;
        const effectiveAlignment = dynamicViewEnabled ? 'below' : nodeAlignment;
        const effectiveFocusMode = dynamicViewEnabled ? true : focusNodeViewEnabled;
        
        // Filter nodes based on nodeLevel (PathNav filtering)
        let filteredNodes = graph.nodes;
        if (hideH1Nodes || hideH2Nodes) {
          filteredNodes = graph.nodes.filter(n => {
            if (hideH1Nodes && n.nodeLevel === 'h1') return false;
            if (hideH2Nodes && n.nodeLevel === 'h2') return false;
            return true;
          });
        }
        
        const response = await fetch(`${API_BASE_URL}/api/graph/layout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nodes: filteredNodes.map(n => ({
              id: n.id,
              name: n.name || n.id,
              title: n.title || n.name || n.id,
              body: n.body || ''
            })),
            collapsed_nodes: Array.from(collapsedNodes),
            expanded_descriptions: expandedNodeIds,
            node_width: currentNodeWidth,
            node_height_full: currentNodeHeightFull,
            node_height_compact: currentNodeHeightCompact,
            horizontal_gap: currentHorizontalGap,
            vertical_gap: currentVerticalGap,
            direction: effectiveDirection,
            alignment: effectiveAlignment,
            show_description: useCompactFocus ? false : showDescription,  // No descriptions in compact focus
            description_char_limit: descriptionCharLimit,
            focus_mode: effectiveFocusMode,
            focus_node_id: focusNodeId,
            focus_layer_depth: focusLayerDepth,
            dynamic_view: dynamicViewEnabled,
            dynamic_view_size: dynamicViewSize
          })
        });
        
        console.log('[VisualEditor] Layout request sent with direction:', effectiveDirection, 'alignment:', effectiveAlignment, 'dynamicView:', dynamicViewEnabled);

        if (!response.ok) {
          throw new Error(`Layout API error: ${response.status}`);
        }

        const data = await response.json();
        
        // Transform API response to component format
        const transformedNodes = data.nodes.map(n => ({
          ...graph.nodes.find(gn => gn.id === n.id),
          x: n.x,
          y: n.y,
          level: n.level,
          isCollapsed: n.is_collapsed,
          hasChildren: n.has_children,
          childCount: n.child_count
        }));

        const transformedEdges = data.edges.map(e => ({
          source: e.source,
          target: e.target,
          fromX: e.from_x,
          fromY: e.from_y,
          toX: e.to_x,
          toY: e.to_y,
          isReturn: e.is_return
        }));

        // Build children map from API response
        const childrenMap = new Map();
        data.nodes.forEach(n => {
          if (n.children && n.children.length > 0) {
            childrenMap.set(n.id, n.children);
          }
        });

        setLayoutedNodes(transformedNodes);
        setEdges(transformedEdges);
        setCanvasSize(data.canvas_size);
        setNodeChildrenMap(childrenMap);
        
        // Center on Start node after initial import
        if (centerOnStartPending && containerRef.current) {
          const startNode = transformedNodes.find(n => n.id === 'Start' || n.name === 'Start');
          if (startNode) {
            const containerHeight = containerRef.current.getBoundingClientRect().height;
            const scale = zoom / 100;
            // Position Start 10% from left, vertically centered (offset by node height)
            const nodeHeight = 40; // compact node height
            const targetPanX = 50;
            const targetPanY = (containerHeight / 2) - ((startNode.y + nodeHeight/2) * scale);
            setPanOffset({ x: targetPanX, y: targetPanY });
          }
          setCenterOnStartPending(false);
        }
        
        // Center the focus node if focusTravelCenter is enabled
        if (focusTravelCenter && focusNodeId && containerRef.current) {
          const focusNode = transformedNodes.find(n => n.id === focusNodeId);
          if (focusNode) {
            const containerWidth = containerRef.current.getBoundingClientRect().width;
            const containerCenterX = containerWidth / 2;
            
            // Get node width based on compact mode
            const useCompact = (focusNodeViewEnabled && focusNodeId) || dynamicViewEnabled;
            const nodeWidth = useCompact ? NODE_WIDTH_COMPACT_FOCUS : NODE_WIDTH;
            
            // Calculate node center position in canvas coordinates
            const nodeCenterX = focusNode.x + (nodeWidth / 2);
            
            // Transform: translate(pan) then scale(zoom) means screenX = (nodeX * scale) + panX
            // To center: containerCenterX = (nodeCenterX * scale) + panX
            // So: panX = containerCenterX - (nodeCenterX * scale)
            const scale = zoom / 100;
            const targetPanX = containerCenterX - (nodeCenterX * scale);
            setPanOffset({ x: targetPanX, y: 50 });
          }
        }

      } catch (error) {
        console.error('Layout API error, using fallback:', error);
        // Fallback: simple vertical layout
        const currentNodeHeight = showDescription ? NODE_HEIGHT_FULL : NODE_HEIGHT_COMPACT;
        const fallbackNodes = graph.nodes.map((node, index) => ({
          ...node,
          x: 50,
          y: index * (currentNodeHeight + VERTICAL_GAP),
          level: 0,
          isCollapsed: collapsedNodes.has(node.id),
          hasChildren: false,
          childCount: 0
        }));
        
        setLayoutedNodes(fallbackNodes);
        setEdges([]);
        setCanvasSize({ 
          width: 400, 
          height: graph.nodes.length * (currentNodeHeight + VERTICAL_GAP) + 100 
        });
        setNodeChildrenMap(new Map());
      } finally {
        setIsCalculatingLayout(false);
      }
    };

    calculateLayoutViaAPI();
  }, [graph.nodes, collapsedNodes, expandedDescriptions, layoutDirection, nodeAlignment, showDescription, descriptionCharLimit, focusNodeViewEnabled, focusTravelCenter, focusLayerDepth, dynamicViewEnabled, dynamicViewSize, layoutFocusNodeId, hideH1Nodes, hideH2Nodes, nodeWidthPaddingEnabled, nodeWidthPaddingPercent]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleZoomSelect = useCallback((value) => {
    setZoom(value);
    setShowZoomDropdown(false);
  }, []);

  const handleResetView = useCallback(() => {
    // On mobile, use smaller zoom to fit more content
    const isMobile = window.innerWidth < 768;
    setZoom(isMobile ? 60 : 100);
    setPanOffset({ x: 20, y: 20 });
    // Also center on Start node
    const startNode = graph.nodes.find(n => n.id === 'Start');
    if (startNode) {
      setFocusNodeId('Start');
    }
  }, [graph.nodes]);

  // Pan handlers (mouse)
  const handleMouseDown = useCallback((e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg' || e.target.closest('.canvas-bg')) {
      setIsPanning(true);
      setStartPan({ x: e.clientX, y: e.clientY });
      setStartOffset({ ...panOffset });
    }
  }, [panOffset]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    const dx = e.clientX - startPan.x;
    const dy = e.clientY - startPan.y;
    setPanOffset({ x: startOffset.x + dx, y: startOffset.y + dy });
  }, [isPanning, startPan, startOffset]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsPanning(true);
      setStartPan({ x: touch.clientX, y: touch.clientY });
      setStartOffset({ ...panOffset });
    } else if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setLastPinchDistance(distance);
    }
  }, [panOffset]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isPanning) {
      const touch = e.touches[0];
      const dx = touch.clientX - startPan.x;
      const dy = touch.clientY - startPan.y;
      setPanOffset({ x: startOffset.x + dx, y: startOffset.y + dy });
    } else if (e.touches.length === 2 && lastPinchDistance) {
      e.preventDefault();
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const delta = distance - lastPinchDistance;
      // Reduced zoom sensitivity for mobile - was 0.5, now 0.15
      const zoomDelta = delta * 0.15;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + zoomDelta)));
      setLastPinchDistance(distance);
    }
  }, [isPanning, startPan, startOffset, lastPinchDistance]);

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    setLastPinchDistance(null);
  }, []);
  
  // Double-tap on canvas to go to Start/Home node
  const lastTapRef = useRef(0);
  const handleCanvasDoubleTap = useCallback((e) => {
    // Check if tapping on blank area (not a node)
    if (e.target.closest('.node-group') || e.target.closest('g[data-node-id]')) return;
    
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap detected - go to Start node
      const startNode = graph.nodes.find(n => n.id === 'Start');
      if (startNode) {
        setFocusNodeId('Start');
        // Center on Start node
        const canvasCenter = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setPanOffset({
          x: canvasCenter.x - (startNode.x * zoom / 100),
          y: canvasCenter.y - (startNode.y * zoom / 100)
        });
        toast({ title: 'Centered on Home', description: 'Returned to Start node' });
      }
    }
    lastTapRef.current = now;
  }, [graph.nodes, zoom]);

  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -10 : 10;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta)));
    }
  }, []);
  
  const handleImportTwee = () => {
    if (!tweeInput.trim()) {
      toast({ title: 'Empty input', description: 'Please paste some Twee code first.', variant: 'destructive' });
      return;
    }
    
    try {
      resetNodeCounter();
      const parsedGraph = parseTweeToGraph(tweeInput);
      
      if (parsedGraph.nodes.length === 0) {
        toast({ title: 'No passages found', description: 'Make sure your Twee code has passages starting with "::".', variant: 'destructive' });
        return;
      }
      
      setGraph(parsedGraph);
      setShowCanvas(true);
      setZoom(70);
      setPanOffset({ x: 50, y: 50 });
      setCollapsedNodes(new Set());
      
      // Apply editor config from imported Twee file if present
      if (parsedGraph.editorConfig) {
        applyEditorConfig(parsedGraph.editorConfig);
      }
      
      toast({ title: 'Story imported!', description: `Loaded ${parsedGraph.nodes.length} passages.` });
    } catch (error) {
      console.error('Import error:', error);
      toast({ title: 'Import failed', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleStartEmpty = () => {
    resetNodeCounter();
    const startNode = {
      id: 'Start',
      name: 'Start',
      body: 'Your story begins here...',
      x: 100,
      y: 200
    };
    
    setGraph({ nodes: [startNode], edges: [], storyTitle: 'My Interactive Story' });
    setShowCanvas(true);
    setZoom(70);
    setPanOffset({ x: 50, y: 50 });
    setCollapsedNodes(new Set());
    
    toast({ title: 'New story started', description: 'Double-click the Start node to begin editing.' });
  };
  
  const handleNodeDoubleClick = (node) => {
    setEditingNode(node);
    setIsEditModalOpen(true);
  };
  
  const handleSaveNode = (updatedNode, linkedNodes) => {
    // Save current state to undo history
    saveToUndoHistory(graph);
    
    let updatedNodes = graph.nodes.map(n => n.id === updatedNode.id ? updatedNode : n);
    
    if (linkedNodes && linkedNodes.length > 0) {
      linkedNodes.forEach(ln => {
        if (!updatedNodes.find(n => n.id === ln.id)) {
          updatedNodes.push(ln);
        }
      });
    }
    
    const allEdges = [];
    updatedNodes.forEach(node => {
      const linkRegex = /\[\[([^\]]+)\]\]/g;
      let match;
      
      while ((match = linkRegex.exec(node.body)) !== null) {
        const linkContent = match[1];
        let targetName;
        
        if (linkContent.includes('->')) {
          targetName = linkContent.split('->')[1].trim();
        } else if (linkContent.includes('|')) {
          targetName = linkContent.split('|')[1].trim();
        } else {
          targetName = linkContent.trim();
        }
        
        const targetNode = updatedNodes.find(n => n.name === targetName || n.id === targetName);
        if (targetNode) {
          allEdges.push({ source: node.id, target: targetNode.id });
        }
      }
    });
    
    setGraph({ ...graph, nodes: updatedNodes, edges: allEdges });
    setIsEditModalOpen(false);
    setEditingNode(null);
    
    toast({ title: 'Node updated', description: `"${updatedNode.name}" has been saved.` });
  };
  
  const handleCreateNode = () => {
    const newName = generateNodeName();
    const newNode = { id: newName, name: newName, body: 'New passage content...', x: 0, y: 0 };
    
    setGraph({ ...graph, nodes: [...graph.nodes, newNode] });
    toast({ title: 'Node created', description: `New passage "${newName}" added.` });
    
    return newName;
  };

  // Create a child node connected to the specified parent node
  // Uses functional state update to always get the latest graph state
  const handleCreateChildNode = useCallback((parentNodeId) => {
    const newName = generateNodeName();
    let parentNodeName = parentNodeId; // fallback for toast
    
    // Save current state to undo history
    saveToUndoHistory(graph);
    
    setGraph(prevGraph => {
      const parentNode = prevGraph.nodes.find(n => n.id === parentNodeId);
      if (!parentNode) {
        console.warn('[handleCreateChildNode] Parent node not found in current state:', parentNodeId);
        console.log('[handleCreateChildNode] Available nodes:', prevGraph.nodes.map(n => n.id));
        return prevGraph; // Return unchanged state
      }
      
      parentNodeName = parentNode.name;
      
      const newNode = { 
        id: newName, 
        name: newName, 
        body: 'New passage content...', 
        x: 0, 
        y: 0 
      };
      
      // Create edge from parent to new node
      const newEdge = {
        source: parentNodeId,
        target: newName,
        from: parentNodeId,
        to: newName
      };
      
      // Also add a link in the parent's body
      const linkText = `[[${newName}]]`;
      const updatedParent = {
        ...parentNode,
        body: parentNode.body ? `${parentNode.body}\n\n${linkText}` : linkText
      };
      
      // Update nodes: replace parent with updated parent, add new node
      const updatedNodes = prevGraph.nodes.map(n => n.id === parentNodeId ? updatedParent : n);
      
      return { 
        ...prevGraph, 
        nodes: [...updatedNodes, newNode], 
        edges: [...(prevGraph.edges || []), newEdge] 
      };
    });
    
    // Set focus to the new node
    setFocusNodeId(newName);
    
    toast({ 
      title: 'Child node created', 
      description: `"${newName}" connected to "${parentNodeName}"` 
    });
    
    return newName;
  }, [toast]);

  // State for add-child menu popup
  const [showAddChildMenu, setShowAddChildMenu] = useState(false);
  const [addChildMenuPosition, setAddChildMenuPosition] = useState({ x: 0, y: 0 });
  const [addChildParentId, setAddChildParentId] = useState(null);
  const addChildTimerRef = useRef(null);
  
  // Reparent mode state - for click-to-select reparenting
  const [reparentMode, setReparentMode] = useState(false);
  const [reparentNodeId, setReparentNodeId] = useState(null);
  const [showReparentConfirm, setShowReparentConfirm] = useState(false);
  const [reparentTargetId, setReparentTargetId] = useState(null);
  const [reparentConfirmPosition, setReparentConfirmPosition] = useState({ x: 0, y: 0 });

  // Handle press-and-hold on add child button
  const handleAddChildPressStart = (nodeId, clientX, clientY) => {
    addChildTimerRef.current = setTimeout(() => {
      setAddChildParentId(nodeId);
      setAddChildMenuPosition({ x: clientX, y: clientY });
      setShowAddChildMenu(true);
    }, 500); // 500ms hold to show menu
  };

  const handleAddChildPressEnd = (nodeId) => {
    if (addChildTimerRef.current) {
      clearTimeout(addChildTimerRef.current);
      addChildTimerRef.current = null;
    }
    // If menu not showing, it was a quick tap - create child immediately
    if (!showAddChildMenu) {
      handleCreateChildNode(nodeId);
    }
  };

  const handleAddChildPressCancel = () => {
    if (addChildTimerRef.current) {
      clearTimeout(addChildTimerRef.current);
      addChildTimerRef.current = null;
    }
  };

  // ============= REPARENT MODE FUNCTIONS =============
  
  // Enter reparent mode - user will click on target node
  const enterReparentMode = (nodeId) => {
    setReparentNodeId(nodeId);
    setReparentMode(true);
    setShowAddChildMenu(false);
    toast({ 
      title: 'Reparent Mode', 
      description: 'Click on a node to move this node under it, or press Escape to cancel' 
    });
  };
  
  // Cancel reparent mode
  const cancelReparentMode = () => {
    setReparentMode(false);
    setReparentNodeId(null);
    setShowReparentConfirm(false);
    setReparentTargetId(null);
  };
  
  // Handle click on a node during reparent mode
  const handleReparentTargetClick = (targetNodeId, clientX, clientY) => {
    if (!reparentMode || !reparentNodeId) return;
    
    // Can't reparent to self or to Start
    if (targetNodeId === reparentNodeId) {
      toast({ title: 'Invalid target', description: 'Cannot move node to itself', variant: 'destructive' });
      return;
    }
    
    // Check if target is a descendant of the node being moved
    const descendants = getDescendantIds(reparentNodeId);
    if (descendants.includes(targetNodeId)) {
      toast({ title: 'Invalid target', description: 'Cannot move node under its own child', variant: 'destructive' });
      return;
    }
    
    // Show confirmation popup
    setReparentTargetId(targetNodeId);
    setReparentConfirmPosition({ x: clientX, y: clientY });
    setShowReparentConfirm(true);
  };
  
  // Confirm reparent action
  const confirmReparent = () => {
    if (!reparentNodeId || !reparentTargetId) return;
    
    // Save current state to undo history
    saveToUndoHistory(graph);
    
    const updatedGraph = reparentNode(graph, reparentNodeId, reparentTargetId);
    setGraph(updatedGraph);
    
    // Immediately update Twee code from the new graph
    const updatedTwee = graphToTwee(updatedGraph);
    setTweeInput(updatedTwee);
    localStorage.setItem('visual_editor_twee', updatedTwee);
    
    const targetNode = graph.nodes.find(n => n.id === reparentTargetId);
    toast({ 
      title: 'Node moved', 
      description: `Moved under "${targetNode?.name || reparentTargetId}"` 
    });
    
    cancelReparentMode();
  };
  
  // Handle keyboard escape to cancel reparent mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && reparentMode) {
        cancelReparentMode();
        toast({ title: 'Cancelled', description: 'Reparent mode cancelled' });
      }
    };
    
    if (reparentMode) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [reparentMode]);

  // ============= DRAG & DROP REPARENTING =============
  
  // Get all descendant IDs of a node (to prevent dropping on own children)
  const getDescendantIds = useCallback((nodeId, visited = new Set()) => {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);
    
    // Get children from edges, but EXCLUDE "Back" links which go TO parents
    // A real child edge goes from parent to child, not from child back to parent
    const childIds = (graph.edges || [])
      .filter(e => {
        if (e.source !== nodeId) return false;
        // Exclude edges that are "Back" links (label contains "Back")
        if (e.label && (e.label.toLowerCase().includes('back') || e.label === 'Back')) return false;
        return true;
      })
      .map(e => e.target);
    
    let descendants = [...childIds];
    childIds.forEach(childId => {
      descendants = [...descendants, ...getDescendantIds(childId, visited)];
    });
    
    return descendants;
  }, [graph.edges]);

  // Start drag detection (press and hold)
  const handleNodeDragStart = useCallback((nodeId, clientX, clientY, isRightClick = false) => {
    // Clear any existing timers
    if (dragTimerRef.current) clearTimeout(dragTimerRef.current);
    if (holdProgressRef.current) clearInterval(holdProgressRef.current);
    
    // Right-click immediately enters drag mode
    if (isRightClick) {
      setDragState({
        isDragging: true,
        draggedNodeId: nodeId,
        dragStartPos: { x: clientX, y: clientY },
        currentPos: { x: clientX, y: clientY },
        dropTargetId: null,
        holdProgress: 100
      });
      return;
    }
    
    // Start hold progress
    let progress = 0;
    holdProgressRef.current = setInterval(() => {
      progress += 10;
      setDragState(prev => ({ ...prev, holdProgress: Math.min(100, progress) }));
    }, DRAG_HOLD_DELAY / 10);
    
    // After hold delay, enter drag mode
    dragTimerRef.current = setTimeout(() => {
      if (holdProgressRef.current) clearInterval(holdProgressRef.current);
      setDragState({
        isDragging: true,
        draggedNodeId: nodeId,
        dragStartPos: { x: clientX, y: clientY },
        currentPos: { x: clientX, y: clientY },
        dropTargetId: null,
        holdProgress: 100
      });
    }, DRAG_HOLD_DELAY);
  }, []);

  // Cancel drag
  const handleNodeDragCancel = useCallback(() => {
    if (dragTimerRef.current) clearTimeout(dragTimerRef.current);
    if (holdProgressRef.current) clearInterval(holdProgressRef.current);
    setDragState({
      isDragging: false,
      draggedNodeId: null,
      dragStartPos: null,
      currentPos: null,
      dropTargetId: null,
      holdProgress: 0
    });
  }, []);

  // Update drag position
  const handleNodeDragMove = useCallback((clientX, clientY) => {
    if (!dragState.isDragging) return;
    setDragState(prev => ({
      ...prev,
      currentPos: { x: clientX, y: clientY }
    }));
  }, [dragState.isDragging]);

  // Set drop target when hovering over a node
  const handleSetDropTarget = useCallback((nodeId) => {
    if (!dragState.isDragging) return;
    if (nodeId === dragState.draggedNodeId) return; // Can't drop on self
    
    // Check if target is a descendant of dragged node
    const descendants = getDescendantIds(dragState.draggedNodeId);
    if (descendants.includes(nodeId)) return; // Can't drop on own children
    
    setDragState(prev => ({ ...prev, dropTargetId: nodeId }));
  }, [dragState.isDragging, dragState.draggedNodeId, getDescendantIds]);

  // Clear drop target
  const handleClearDropTarget = useCallback(() => {
    setDragState(prev => ({ ...prev, dropTargetId: null }));
  }, []);

  // Complete the drop - reparent the node
  const handleNodeDrop = useCallback(() => {
    if (!dragState.isDragging || !dragState.dropTargetId) {
      handleNodeDragCancel();
      return;
    }
    
    const draggedNodeId = dragState.draggedNodeId;
    const newParentId = dragState.dropTargetId;
    
    // Reparent: Update the graph to create edge from new parent to dragged node
    setGraph(prevGraph => {
      const draggedNode = prevGraph.nodes.find(n => n.id === draggedNodeId);
      const newParent = prevGraph.nodes.find(n => n.id === newParentId);
      
      if (!draggedNode || !newParent) return prevGraph;
      
      // Remove old edges pointing to the dragged node
      const filteredEdges = (prevGraph.edges || []).filter(e => e.target !== draggedNodeId);
      
      // Create new edge from new parent to dragged node
      const newEdge = {
        source: newParentId,
        target: draggedNodeId,
        from: newParentId,
        to: draggedNodeId
      };
      
      // Add link in new parent's body
      const linkText = `[[${draggedNodeId}]]`;
      const updatedParent = {
        ...newParent,
        body: newParent.body ? `${newParent.body}\n${linkText}` : linkText
      };
      
      // Update nodes array - move dragged node after new parent
      const otherNodes = prevGraph.nodes.filter(n => n.id !== draggedNodeId && n.id !== newParentId);
      const parentIndex = prevGraph.nodes.findIndex(n => n.id === newParentId);
      
      const newNodes = [...otherNodes];
      // Insert parent and dragged node in correct order
      newNodes.splice(parentIndex > newNodes.length ? newNodes.length : parentIndex, 0, updatedParent, draggedNode);
      
      return {
        ...prevGraph,
        nodes: newNodes.filter((n, i, arr) => arr.findIndex(x => x.id === n.id) === i), // Remove duplicates
        edges: [...filteredEdges, newEdge]
      };
    });
    
    toast({
      title: 'Node Moved',
      description: `"${draggedNodeId}" is now a child of "${newParentId}"`
    });
    
    // Reset drag state
    handleNodeDragCancel();
  }, [dragState, handleNodeDragCancel, toast]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (dragTimerRef.current) clearTimeout(dragTimerRef.current);
      if (holdProgressRef.current) clearInterval(holdProgressRef.current);
    };
  }, []);
  
  // PathNav Sitemap Mode toggle handler
  const handlePathNavModeToggle = (checked) => {
    setPathNavSitemapMode(checked);
    if (checked) {
      // Auto-configure for PathNav mode
      setFocusNodeViewEnabled(true);
      setFocusLayerDepth(2);
      setDynamicViewEnabled(true);
      setDynamicViewSize('standard');
    }
    // When unchecked, leave settings as-is (user can adjust manually)
  };

  // Fallback copy function for when Clipboard API is blocked
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fallback for when Clipboard API is blocked (e.g., in iframes)
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        return true;
      } catch (copyErr) {
        console.error('Fallback copy failed:', copyErr);
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  // Export PathNav Sitemap for WordPress headers
  const handleExportPathNav = async () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before exporting.', variant: 'destructive' });
      return;
    }
    try {
      // Use the new function that takes graph directly (like WordPress export)
      const html = generatePathNavHtml(graph, {
        nodeFormat: nodeFormat,
        showDescription: showDescription,
        descriptionCharLimit: 80,
        focusBorderColor: pathNavFocusColor,
        startMinimized: pathNavStartMinimized,
        storageKey: 'pathnav_minimized'
      });
      const success = await copyToClipboard(html);
      if (success) {
        toast({ 
          title: 'PathNav Sitemap Copied!', 
          description: 'Paste into WordPress header (Custom HTML block or WPCode plugin).' 
        });
      } else {
        toast({ title: 'Export Failed', description: 'Unable to copy to clipboard.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleExportTwee = () => {
    // Include editorConfig in the graph for persistence
    const graphWithConfig = {
      ...graph,
      editorConfig: buildEditorConfig()
    };
    const tweeText = graphToTwee(graphWithConfig);
    const blob = new Blob([tweeText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'story.twee';
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Twee exported', description: 'Your story has been saved as story.twee (with editor settings)' });
  };
  
  const handleCopyTwee = async () => {
    // Include editorConfig in the graph for persistence
    const graphWithConfig = {
      ...graph,
      editorConfig: buildEditorConfig()
    };
    const tweeText = graphToTwee(graphWithConfig);
    const success = await copyToClipboard(tweeText);
    if (success) {
      toast({ title: 'Copied!', description: 'Twee code copied to clipboard (with editor settings).' });
    } else {
      toast({ title: 'Copy Failed', description: 'Unable to copy to clipboard.', variant: 'destructive' });
    }
  };

  // Export for WordPress - generates StoryNav config JSON
  const handleExportWordPress = () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before exporting.', variant: 'destructive' });
      return;
    }

    // Build hierarchical node structure from graph
    const buildNodeTree = (nodes, edges) => {
      // Create a map of parent -> children relationships from edges
      const childrenMap = new Map();
      const hasParent = new Set();
      
      edges.forEach(edge => {
        const parentId = edge.source || edge.from;
        const childId = edge.target || edge.to;
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId).push(childId);
        hasParent.add(childId);
      });

      // Find root nodes (nodes without parents)
      const rootNodes = nodes.filter(n => !hasParent.has(n.id));

      // Recursively build tree structure
      const buildNode = (node) => {
        const treeNode = {
          id: node.id,
          label: node.name || node.id,
        };

        // Add link if node has a URL in body
        const urlMatch = node.body?.match(/URL:\s*(https?:\/\/[^\s\]]+)/i);
        if (urlMatch) {
          treeNode.link = urlMatch[1];
        }

        // Add children
        const childIds = childrenMap.get(node.id) || [];
        if (childIds.length > 0) {
          treeNode.children = childIds
            .map(id => nodes.find(n => n.id === id))
            .filter(Boolean)
            .map(buildNode);
        }

        return treeNode;
      };

      return rootNodes.map(buildNode);
    };

    const nodeTree = buildNodeTree(graph.nodes, graph.edges || []);

    // Build config object
    const config = {
      id: graph.storyTitle?.toLowerCase().replace(/\s+/g, '-') || 'storynav',
      nodes: nodeTree,
      // Include current dynamic view settings
      nodeWidth: dynamicViewSize === 'compact' ? 112 : 160,
      nodeHeight: dynamicViewSize === 'compact' ? 28 : 36,
      fontSize: dynamicViewSize === 'compact' ? 9 : 11
    };

    const configJson = JSON.stringify(config);
    
    // Generate the shortcode
    const shortcode = `[storynav config='${configJson}']`;

    // Copy to clipboard
    navigator.clipboard.writeText(shortcode).then(() => {
      toast({ 
        title: 'WordPress Shortcode Copied!', 
        description: 'Paste this in your WordPress page or plugin settings.' 
      });
    }).catch(() => {
      // Fallback: show in a prompt
      prompt('Copy this WordPress shortcode:', shortcode);
    });
  };

  // Download WordPress plugin ZIP (links to download location)
  const handleDownloadPlugin = () => {
    window.open('/wordpress-plugin/tweeporter-storynav.zip', '_blank');
    toast({ 
      title: 'Plugin Download', 
      description: 'If download doesn\'t start, the plugin may still be generating.' 
    });
  };

  // Save project as .jsont file with file picker dialog
  const handleSaveProject = async () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before saving.', variant: 'destructive' });
      return;
    }
    
    // Build settings including all editor config
    const settings = {
      source: localStorage.getItem('visual_editor_source') || 'manual',
      nodeFormats: nodeFormat,
      layoutDirection,
      showDescription,
      showBackLinks,
      hideH1Nodes,
      hideH2Nodes,
      focusLayerDepth,
      dynamicViewSize,
      // Include all editor config settings
      ...buildEditorConfig()
    };
    
    // Generate project name for filename
    const projectName = (graph.storyTitle || 'project')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const date = new Date().toISOString().split('T')[0];
    const suggestedName = `${projectName}-${date}.jsont`;
    
    // Check if we're in an iframe (downloads are often blocked)
    const isInIframe = window !== window.top;
    
    // Prepare the JSON content
    const json = serializeProject(graph, settings);
    console.log('[VisualEditor] Save - serialized JSON length:', json.length);
    console.log('[VisualEditor] Save - first 200 chars:', json.substring(0, 200));
    
    // Use File System Access API if available and not in iframe
    if ('showSaveFilePicker' in window && !isInIframe) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: suggestedName,
          types: [{
            description: 'TweePorter Project',
            accept: { 'application/json': ['.jsont'] }
          }]
        });
        
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
        
        toast({ title: 'Project Saved!', description: `Saved as ${handle.name}` });
        return;
      } catch (err) {
        if (err.name === 'AbortError') {
          return; // User cancelled
        }
        console.log('File picker unavailable, trying download fallback');
      }
    }
    
    // Create blob and try download
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    
    // Cleanup after delay
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    
    // If in iframe, warn user that download may be blocked
    if (isInIframe) {
      // Also copy to clipboard as backup
      try {
        await navigator.clipboard.writeText(json);
        toast({ 
          title: 'Save Attempted', 
          description: 'If download didn\'t start, the project was copied to clipboard. Open app in new tab for reliable saves.',
          duration: 8000
        });
      } catch (clipErr) {
        toast({ 
          title: 'Save Attempted', 
          description: 'If download didn\'t start, please open the app in a new browser tab (not preview iframe).',
          duration: 6000
        });
      }
    } else {
      toast({ title: 'Project Saved!', description: `Downloaded as ${suggestedName}` });
    }
  };

  // Import project from .jsont file
  const fileInputRef = useRef(null);
  const handleImportProject = async () => {
    // Check if we're in an iframe (File System API won't work)
    const isInIframe = window !== window.top;
    
    // Use File System Access API if available and not in iframe
    if ('showOpenFilePicker' in window && !isInIframe) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [{
            description: 'TweePorter Project',
            accept: { 'application/json': ['.jsont', '.json'] }
          }],
          multiple: false
        });
        
        const file = await handle.getFile();
        await processImportedFile(file);
      } catch (err) {
        // User cancelled or cross-origin error
        if (err.name === 'AbortError') {
          return; // User cancelled
        }
        // Fall back to file input for other errors
        console.log('File picker unavailable, using input fallback');
        fileInputRef.current?.click();
      }
    } else {
      // Fallback: use file input (works in iframes and older browsers)
      fileInputRef.current?.click();
    }
  };
  
  // Process imported file (used by both File System API and fallback)
  const processImportedFile = async (file) => {
    try {
      const projectData = await importProjectFromFile(file);
      setGraph(projectData.graph);
      setTweeInput(projectData.twee);
      if (projectData.settings) {
        // Apply all settings including editorConfig fields
        applyEditorConfig(projectData.settings);
        
        // Also apply any settings not covered by editorConfig
        if (projectData.settings.layoutDirection) setLayoutDirection(projectData.settings.layoutDirection);
        if (projectData.settings.showDescription !== undefined) setShowDescription(projectData.settings.showDescription);
        if (projectData.settings.showBackLinks !== undefined) setShowBackLinks(projectData.settings.showBackLinks);
        if (projectData.settings.hideH1Nodes !== undefined) setHideH1Nodes(projectData.settings.hideH1Nodes);
        if (projectData.settings.hideH2Nodes !== undefined) setHideH2Nodes(projectData.settings.hideH2Nodes);
        if (projectData.settings.focusLayerDepth) setFocusLayerDepth(projectData.settings.focusLayerDepth);
        if (projectData.settings.dynamicViewSize) setDynamicViewSize(projectData.settings.dynamicViewSize);
        if (projectData.settings.nodeFormats) setNodeFormat(projectData.settings.nodeFormats);
      }
      setShowCanvas(true);
      toast({ title: 'Project Imported!', description: `Loaded ${projectData.graph.nodes.length} nodes from ${projectData.projectName}` });
    } catch (error) {
      toast({ title: 'Import Failed', description: error.message, variant: 'destructive' });
    }
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processImportedFile(file);
    e.target.value = ''; // Reset input
  };

  // Export No-JS HTML (CSS :target based, with link nodes)
  const handleExportNoJs = async () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before exporting.', variant: 'destructive' });
      return;
    }
    try {
      // Update twee first
      const currentTwee = graphToTwee(graph);
      localStorage.setItem('visual_editor_twee', currentTwee);
      localStorage.setItem('visual_editor_graph', JSON.stringify(graph));
      
      // Filter nodes based on Visual Editor settings
      const filteredNodes = graph.nodes.filter(node => {
        if (hideH1Nodes && node.nodeLevel === 'h1') return false;
        if (hideH2Nodes && node.nodeLevel === 'h2') return false;
        if (!showBackLinks && node.name?.toLowerCase().includes('back')) return false;
        return true;
      });
      
      // Filter edges to only include visible nodes
      const visibleIds = new Set(filteredNodes.map(n => n.id));
      const filteredEdges = (graph.edges || []).filter(e => {
        const from = e.from || e.source;
        const to = e.to || e.target;
        return visibleIds.has(from) && visibleIds.has(to);
      });
      
      const exportGraph = { ...graph, nodes: filteredNodes, edges: filteredEdges };
      
      // Pass ALL current Visual Editor configuration
      const exportSettings = {
        nodeFormat,
        layoutDirection,
        nodeAlignment,
        showDescription,
        descriptionCharLimit,
        showBackLinks,
        hideH1Nodes,
        hideH2Nodes,
        focusNodeViewEnabled,
        focusLayerDepth,
        dynamicViewEnabled,
        dynamicViewSize
      };
      
      await copyNoJsHtml(exportGraph, exportSettings);
      toast({ title: 'No-JS HTML Copied!', description: 'Paste into any HTML block. Works without JavaScript.' });
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };

  // Export WordPress JS-enabled HTML
  const handleExportWpJs = async () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before exporting.', variant: 'destructive' });
      return;
    }
    try {
      // Update twee first
      const currentTwee = graphToTwee(graph);
      localStorage.setItem('visual_editor_twee', currentTwee);
      localStorage.setItem('visual_editor_graph', JSON.stringify(graph));
      
      // Filter nodes based on Visual Editor settings
      const filteredNodes = graph.nodes.filter(node => {
        if (hideH1Nodes && node.nodeLevel === 'h1') return false;
        if (hideH2Nodes && node.nodeLevel === 'h2') return false;
        if (!showBackLinks && node.name?.toLowerCase().includes('back')) return false;
        return true;
      });
      
      // Filter edges to only include visible nodes
      const visibleIds = new Set(filteredNodes.map(n => n.id));
      const filteredEdges = (graph.edges || []).filter(e => {
        const from = e.from || e.source;
        const to = e.to || e.target;
        return visibleIds.has(from) && visibleIds.has(to);
      });
      
      const exportGraph = { ...graph, nodes: filteredNodes, edges: filteredEdges };
      
      // Pass ALL current Visual Editor configuration
      const exportSettings = {
        nodeFormat,
        layoutDirection,
        nodeAlignment,
        showDescription,
        descriptionCharLimit,
        showBackLinks,
        hideH1Nodes,
        hideH2Nodes,
        focusNodeViewEnabled,
        focusLayerDepth,
        dynamicViewEnabled,
        dynamicViewSize
      };
      
      await copyWordPressHtml(exportGraph, exportSettings);
      toast({ title: 'WordPress HTML Copied!', description: 'Paste into WordPress Custom HTML block.' });
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };

  // Export WordPress Twee-Story (interactive story for WordPress)
  const handleExportWpStory = async () => {
    if (!graph.nodes || graph.nodes.length === 0) {
      toast({ title: 'No content', description: 'Add some nodes before exporting.', variant: 'destructive' });
      return;
    }
    try {
      // Convert graph to Twee format
      const currentTwee = graphToTwee(graph);
      
      // Export settings (can be customized later)
      const storySettings = {
        linkColor: '#667eea',
        textBgColor: '#ffffff',
        frameBgColor: '#f8fafc',
        boldColor: '#fbd38d',
        italicColor: '#b794f4',
        headingColor: '#90cdf4',
        linkDisplay: 'buttons',
        containerWidth: '100%',
        maxWidth: '900px'
      };
      
      await copyWordPressStoryHtml(currentTwee, storySettings);
      toast({ title: 'WordPress Story Copied!', description: 'Paste into WordPress Custom HTML block for interactive story.' });
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };

  // Delete node handler
  const handleDeleteNode = useCallback((nodeId, deleteChildren = false) => {
    const node = graph.nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    // Find children
    const childIds = new Set();
    const findChildren = (id) => {
      (graph.edges || []).forEach(edge => {
        const from = edge.from || edge.source;
        const to = edge.to || edge.target;
        if (from === id && !to.toLowerCase().includes('back')) {
          if (!childIds.has(to)) {
            childIds.add(to);
            findChildren(to);
          }
        }
      });
    };
    findChildren(nodeId);
    
    if (childIds.size > 0 && !deleteChildren) {
      toast({ 
        title: 'Cannot Delete', 
        description: `This node has ${childIds.size} children. Check "Delete children" to proceed.`,
        variant: 'destructive'
      });
      return;
    }
    
    // Save to undo history
    saveToUndoHistory(graph);
    
    // Determine which nodes to delete
    const nodesToDelete = deleteChildren ? new Set([nodeId, ...childIds]) : new Set([nodeId]);
    
    // Remove nodes and related edges
    const newNodes = graph.nodes.filter(n => !nodesToDelete.has(n.id));
    const newEdges = (graph.edges || []).filter(e => {
      const from = e.from || e.source;
      const to = e.to || e.target;
      return !nodesToDelete.has(from) && !nodesToDelete.has(to);
    });
    
    setGraph({ ...graph, nodes: newNodes, edges: newEdges });
    setShowAddChildMenu(false);
    
    // Update Twee in localStorage
    const updatedGraph = { ...graph, nodes: newNodes, edges: newEdges };
    const currentTwee = graphToTwee(updatedGraph);
    localStorage.setItem('visual_editor_twee', currentTwee);
    localStorage.setItem('visual_editor_graph', JSON.stringify(updatedGraph));
    
    const count = nodesToDelete.size;
    toast({ title: 'Deleted', description: count > 1 ? `Deleted ${count} nodes` : 'Node deleted' });
  }, [graph, saveToUndoHistory]);

  // State for delete confirmation checkbox
  const [deleteChildrenChecked, setDeleteChildrenChecked] = useState(false);

  // Check if we're in compact focus mode or dynamic view mode
  const isCompactFocusMode = (focusNodeViewEnabled && focusNodeId) || dynamicViewEnabled;
  const isDynamicViewMode = dynamicViewEnabled;

  // Get current dimensions based on mode
  const getCurrentNodeWidth = () => calculatedNodeWidth;
  const getCurrentNodeHeight = () => isCompactFocusMode ? NODE_HEIGHT_COMPACT_FOCUS : (showDescription ? NODE_HEIGHT_FULL : NODE_HEIGHT_COMPACT);

  // Render edges with bezier curves
  const renderEdges = () => {
    // Get edges based on focus view and back links settings
    let visibleEdges = getVisibleEdgesForFocusView();
    
    // Filter out return edges if showBackLinks is false
    if (!showBackLinks) {
      visibleEdges = visibleEdges.filter(e => !e.isReturn);
    }
    
    return visibleEdges.map((edge, index) => {
      // In compact focus mode, use smooth curved connectors without arrows
      if (isCompactFocusMode) {
        // Calculate a smooth S-curve that connects edge-to-edge
        const controlPointOffset = Math.abs(edge.toX - edge.fromX) * 0.4;
        const path = `M ${edge.fromX} ${edge.fromY} C ${edge.fromX + controlPointOffset} ${edge.fromY}, ${edge.toX - controlPointOffset} ${edge.toY}, ${edge.toX} ${edge.toY}`;
        
        return (
          <path
            key={`edge-${index}`}
            d={path}
            fill="none"
            stroke={edge.isReturn ? '#94a3b8' : '#667eea'}
            strokeWidth="2"
            strokeDasharray={edge.isReturn ? '4,4' : 'none'}
            opacity={edge.isReturn ? 0.4 : 0.7}
          />
        );
      }
      
      // Standard mode with arrows
      const midX = (edge.fromX + edge.toX) / 2;
      const path = `M ${edge.fromX} ${edge.fromY} C ${midX} ${edge.fromY}, ${midX} ${edge.toY}, ${edge.toX} ${edge.toY}`;
      
      return (
        <path
          key={`edge-${index}`}
          d={path}
          fill="none"
          stroke={edge.isReturn ? '#94a3b8' : '#667eea'}
          strokeWidth="2"
          strokeDasharray={edge.isReturn ? '5,5' : 'none'}
          markerEnd={edge.isReturn ? 'url(#arrowhead-return)' : 'url(#arrowhead)'}
          opacity={edge.isReturn ? 0.5 : 0.8}
        />
      );
    });
  };

  // Toggle individual node description
  const toggleNodeDescription = (nodeId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Check if a node's description should be shown
  // Individual toggle overrides global setting
  const isNodeDescriptionExpanded = (nodeId) => {
    return expandedDescriptions.has(nodeId);
  };

  // Track click timing for double-click detection
  const clickTimeoutRef = useRef(null);
  
  // Set focus node - with delay to allow double-click to be detected first
  const handleNodeClick = (e, node) => {
    e.stopPropagation();
    
    // Clear any pending single-click action
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    
    // Delay the focus change to allow double-click detection
    clickTimeoutRef.current = setTimeout(() => {
      if (focusNodeId === node.id) {
        // Clicking same node deselects
        setFocusNodeId(null);
      } else {
        setFocusNodeId(node.id);
      }
      clickTimeoutRef.current = null;
    }, 250); // 250ms delay - typical double-click threshold
  };
  
  // Handle node double-click - cancel single-click action and open editor
  const handleNodeDoubleClickWrapper = (e, node) => {
    e.stopPropagation();
    
    // Cancel any pending single-click action
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    
    // Open the edit modal
    handleNodeDoubleClick(node);
  };

  // Get parent node for focus view back navigation
  const getParentNode = (nodeId) => {
    // Find which node has this node as a child
    for (const [parentId, children] of nodeChildrenMap.entries()) {
      if (children && children.includes(nodeId)) {
        return layoutedNodes.find(n => n.id === parentId);
      }
    }
    return null;
  };

  // Filter nodes for focus view
  const getVisibleNodesForFocusView = () => {
    // Dynamic view also enables focus-like filtering
    if ((!focusNodeViewEnabled && !dynamicViewEnabled) || !focusNodeId) {
      return layoutedNodes;
    }
    
    const visibleNodeIds = new Set();
    visibleNodeIds.add(focusNodeId);
    
    // Add parent node
    const parentNode = getParentNode(focusNodeId);
    if (parentNode) {
      visibleNodeIds.add(parentNode.id);
    }
    
    // Add descendants up to focusLayerDepth levels
    // Use children from layoutedNodes directly (more reliable than nodeChildrenMap)
    const addDescendants = (nodeId, depth) => {
      if (depth > focusLayerDepth) return;
      
      // First try nodeChildrenMap, then fall back to finding node in layoutedNodes
      let children = nodeChildrenMap.get(nodeId);
      if (!children || children.length === 0) {
        const node = layoutedNodes.find(n => n.id === nodeId);
        children = node?.children || [];
      }
      
      children.forEach(childId => {
        visibleNodeIds.add(childId);
        addDescendants(childId, depth + 1);
      });
    };
    addDescendants(focusNodeId, 1);
    
    // In dynamic view mode, also add siblings (they get scaled down)
    if (dynamicViewEnabled && parentNode) {
      const siblings = nodeChildrenMap.get(parentNode.id) || [];
      siblings.forEach(sibId => visibleNodeIds.add(sibId));
    }
    
    return layoutedNodes.filter(n => visibleNodeIds.has(n.id));
  };

  // Filter edges for focus view
  const getVisibleEdgesForFocusView = () => {
    if ((!focusNodeViewEnabled && !dynamicViewEnabled) || !focusNodeId) {
      return edges;
    }
    
    const visibleNodes = getVisibleNodesForFocusView();
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    
    return edges.filter(e => 
      visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
    );
  };

  // Check if a node is a sibling of the focused node (same parent)
  const isSiblingOfFocusNode = (nodeId) => {
    if (!focusNodeId || nodeId === focusNodeId) return false;
    
    const focusParent = getParentNode(focusNodeId);
    const nodeParent = getParentNode(nodeId);
    
    // Both have the same parent (or both are root-level nodes)
    if (focusParent && nodeParent) {
      return focusParent.id === nodeParent.id;
    }
    // Both are root-level (no parent)
    if (!focusParent && !nodeParent) {
      return true;
    }
    return false;
  };

  // Get dynamic view scale factor for a node
  const getDynamicViewScale = (node) => {
    if (!dynamicViewEnabled || !focusNodeId) return 1;
    
    const isFocus = node.id === focusNodeId;
    const isSibling = isSiblingOfFocusNode(node.id);
    const isParent = getParentNode(focusNodeId)?.id === node.id;
    
    // Focus node and parent: full scale
    if (isFocus || isParent) return 1;
    
    // Siblings: 50% scale in dynamic view mode
    if (isSibling) return 0.5;
    
    // Children of focus: full scale
    return 1;
  };

  // Render a single node
  const renderNode = (node) => {
    const getPreview = (text) => {
      if (!text) return '';
      // Extract link labels instead of removing them entirely
      // [[Label|Target]] -> Label, [[Target]] -> Target
      const cleaned = text
        .replace(/\[\[([^|\]]+)\|[^\]]+\]\]/g, '$1')  // [[Label|Target]] -> Label
        .replace(/\[\[([^\]]+)\]\]/g, '$1')            // [[Target]] -> Target
        .trim();
      const lines = cleaned.split('\n').filter(line => line.trim());
      const firstLine = lines[0] || '';
      return firstLine.length > descriptionCharLimit ? firstLine.substring(0, descriptionCharLimit) + '...' : firstLine;
    };

    const isStart = node.id === 'Start' || node.name === 'Start';
    const isFocusNode = focusNodeId === node.id;
    
    // Get node style - determines visual appearance
    // nodeStyle can be: 'standard' (dark), 'light' (30% lighter), 'unbounded' (no background)
    const nodeStyle = node.nodeStyle || 'standard';
    
    // Get colors based on node style using nodeFormat configuration
    const getNodeColors = () => {
      if (isStart) {
        return { 
          fill: nodeFormat.start.background, 
          text: nodeFormat.start.text, 
          stroke: nodeFormat.start.border 
        };
      }
      
      switch (nodeStyle) {
        case 'light':
          return { 
            fill: nodeFormat.light.background, 
            text: nodeFormat.light.text, 
            stroke: nodeFormat.light.border 
          };
        case 'unbounded':
          return { 
            fill: nodeFormat.unbounded.background, 
            text: nodeFormat.unbounded.text, 
            stroke: nodeFormat.unbounded.border 
          };
        case 'standard':
        default:
          return { 
            fill: nodeFormat.standard.background, 
            text: nodeFormat.standard.text, 
            stroke: nodeFormat.standard.border 
          };
      }
    };
    
    const colors = getNodeColors();
    const isUnbounded = nodeStyle === 'unbounded';
    
    // Reparent mode visual state
    const isBeingReparented = reparentMode && reparentNodeId === node.id;
    const isReparentTarget = reparentMode && reparentNodeId !== node.id;
    
    // Check if node has a URL
    const hasUrl = node.url && node.url !== '' && !node.url.startsWith('[');
    
    // Get dynamic view scale for sibling scaling effect
    const dynamicScale = getDynamicViewScale(node);
    const isSibling = dynamicViewEnabled && isSiblingOfFocusNode(node.id);
    const siblingOpacity = isSibling ? 0.6 : 1;
    
    // In compact focus mode or dynamic view mode, use smaller dimensions and no descriptions
    if (isCompactFocusMode || isDynamicViewMode) {
      // Use calculated width which respects node padding setting
      const compactWidth = calculatedNodeWidth;
      const compactHeight = dynamicViewSize === 'compact' ? 28 : NODE_HEIGHT_COMPACT_FOCUS;
      const titleMaxChars = dynamicViewSize === 'compact' ? 14 : Math.max(8, Math.floor(compactWidth / 9));
      const fontSize = dynamicViewSize === 'compact' ? 9 : 11;
      const displayName = node.name.length > titleMaxChars 
        ? node.name.substring(0, titleMaxChars) + '...' 
        : node.name;
      
      // Apply dynamic view scale transform
      const scaleTransform = dynamicScale !== 1 
        ? `translate(${node.x}, ${node.y}) scale(${dynamicScale})` 
        : `translate(${node.x}, ${node.y})`;
      
      return (
        <g
          key={node.id}
          transform={scaleTransform}
          style={{ 
            cursor: reparentMode ? (isBeingReparented ? 'move' : 'pointer') : 'pointer',
            opacity: siblingOpacity,
            transition: 'transform 0.2s ease, opacity 0.2s ease'
          }}
          onClick={(e) => {
            if (reparentMode && isReparentTarget) {
              e.stopPropagation();
              handleReparentTargetClick(node.id, e.clientX, e.clientY);
            }
          }}
        >
          {/* Reparent mode - dotted border on node being moved */}
          {isBeingReparented && (
            <rect
              x="-6"
              y="-6"
              width={compactWidth + 12}
              height={compactHeight + 12}
              rx="12"
              fill="none"
              stroke="#f97316"
              strokeWidth="2"
              strokeDasharray="6,4"
              style={{ pointerEvents: 'none' }}
            />
          )}
          
          {/* Focus node outer border - orange in dynamic view */}
          {isFocusNode && !isBeingReparented && (
            <rect
              x="-4"
              y="-4"
              width={compactWidth + 8}
              height={compactHeight + 8}
              rx="12"
              fill="none"
              stroke={dynamicViewEnabled ? '#f97316' : '#f59e0b'}
              strokeWidth="3"
              style={{ pointerEvents: 'none' }}
            />
          )}
          
          {/* Compact node - single colored rectangle with drop shadow */}
          {/* For unbounded style, show just text with optional underline */}
          {!isUnbounded ? (
            <rect
              width={compactWidth}
              height={compactHeight}
              rx="8"
              fill={colors.fill}
              stroke={isReparentTarget ? '#3b82f6' : (isFocusNode ? (dynamicViewEnabled ? '#f97316' : '#f59e0b') : 'transparent')}
              strokeWidth={isReparentTarget ? 2 : (isFocusNode ? 0 : 0)}
              style={{ 
                pointerEvents: 'none',
                filter: dynamicViewEnabled ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' : 'none'
              }}
            />
          ) : (
            /* Unbounded: just an underline indicator */
            <line
              x1="4"
              y1={compactHeight - 2}
              x2={compactWidth - 4}
              y2={compactHeight - 2}
              stroke={colors.text}
              strokeWidth="1"
              strokeDasharray="3,2"
              style={{ pointerEvents: 'none', opacity: 0.5 }}
            />
          )}
          
          {/* Node title - centered, smaller font */}
          <text
            x={compactWidth / 2}
            y={compactHeight / 2 + 1}
            fill={colors.text}
            fontSize={fontSize}
            fontWeight={isUnbounded ? '400' : '500'}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ pointerEvents: 'none', fontStyle: isUnbounded ? 'italic' : 'normal' }}
          >
            {displayName}
          </text>
          
          {/* Has children indicator - arrow in dynamic view, chevron otherwise */}
          {node.hasChildren && !node.isCollapsed && (
            <text
              x={compactWidth - 10}
              y={compactHeight / 2 + 1}
              fill={isUnbounded ? colors.text : 'rgba(255,255,255,0.7)'}
              fontSize={fontSize - 1}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ pointerEvents: 'none', opacity: isUnbounded ? 0.6 : 1 }}
            >
              {dynamicViewEnabled ? '→' : '›'}
            </text>
          )}
          
          {/* URL icon - shows if node has a URL (compact mode) */}
          {hasUrl && !isUnbounded && (
            <g
              transform={`translate(${compactWidth - 18}, 3)`}
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(node.url, '_blank', 'noopener,noreferrer');
              }}
            >
              <circle cx="6" cy="6" r="8" fill="rgba(255,255,255,0.9)" />
              <path
                d="M4.5 7.5L7.5 4.5M7.5 4.5H5M7.5 4.5V7"
                stroke={colors.fill}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <title>Open URL: {node.url}</title>
            </g>
          )}
          
          {/* Invisible clickable overlay - captures clicks and drag for compact node */}
          <rect
            width={compactWidth}
            height={compactHeight}
            rx="8"
            fill="transparent"
            onClick={(e) => {
              // Handle reparent mode click first
              if (reparentMode && isReparentTarget) {
                e.stopPropagation();
                handleReparentTargetClick(node.id, e.clientX, e.clientY);
                return;
              }
              // Only handle click if not actively dragging (holdProgress check removed - it blocks normal clicks)
              if (!dragState.isDragging) {
                handleNodeClick(e, node);
              }
              // Reset drag state on click
              handleNodeDragCancel();
            }}
            onDoubleClick={(e) => handleNodeDoubleClickWrapper(e, node)}
            onMouseDown={(e) => {
              e.stopPropagation(); // Prevent pan from starting
              if (e.button === 2) {
                e.preventDefault();
                handleNodeDragStart(node.id, e.clientX, e.clientY, true);
              } else if (e.button === 0) {
                handleNodeDragStart(node.id, e.clientX, e.clientY, false);
              }
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              if (dragState.isDragging && dragState.dropTargetId) {
                handleNodeDrop();
              }
              // Don't cancel here - let onClick handle it
            }}
            onMouseMove={(e) => {
              if (dragState.isDragging) {
                handleNodeDragMove(e.clientX, e.clientY);
              }
            }}
            onMouseEnter={() => {
              if (dragState.isDragging && dragState.draggedNodeId !== node.id) {
                handleSetDropTarget(node.id);
              }
            }}
            onMouseLeave={() => {
              handleClearDropTarget();
            }}
            onContextMenu={(e) => e.preventDefault()}
            style={{ cursor: dragState.isDragging ? 'grabbing' : 'pointer' }}
          />
          
          {/* Drop target highlight */}
          {dragState.dropTargetId === node.id && (
            <rect
              x="-6"
              y="-6"
              width={compactWidth + 12}
              height={compactHeight + 12}
              rx="14"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="4"
              style={{ pointerEvents: 'none', animation: 'pulse 0.5s ease-in-out infinite' }}
            />
          )}
          
          {/* Hold progress indicator */}
          {dragState.draggedNodeId === node.id && dragState.holdProgress > 0 && dragState.holdProgress < 100 && (
            <g transform={`translate(${compactWidth / 2}, ${compactHeight / 2})`}>
              <circle r="16" fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }} />
              <circle
                r="16"
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
                strokeDasharray={`${(dragState.holdProgress / 100) * 100.53} 100.53`}
                transform="rotate(-90)"
                style={{ pointerEvents: 'none' }}
              />
            </g>
          )}
          
          {/* Add Child Triangle Button - shown when node is focused (AFTER clickable overlay so it's on top) */}
          {isFocusNode && (
            <g
              transform={`translate(${compactWidth + 6}, ${compactHeight / 2})`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // Direct click handler as fallback
                if (!showAddChildMenu) {
                  handleCreateChildNode(node.id);
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleAddChildPressStart(node.id, e.clientX, e.clientY);
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                handleAddChildPressEnd(node.id);
              }}
              onMouseLeave={handleAddChildPressCancel}
              onTouchStart={(e) => {
                e.stopPropagation();
                const touch = e.touches[0];
                handleAddChildPressStart(node.id, touch.clientX, touch.clientY);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                handleAddChildPressEnd(node.id);
              }}
              style={{ cursor: 'pointer' }}
            >
              {/* Triangle pointing right - LEFT edge aligned with outline's RIGHT edge */}
              <polygon
                points="0,-12 18,0 0,12"
                fill="#f59e0b"
                stroke="#d97706"
                strokeWidth="1"
              />
              {/* Plus sign - centered in triangle */}
              <line x1="4" y1="0" x2="12" y2="0" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <line x1="8" y1="-4" x2="8" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <title>Click to add child node (Hold for more options)</title>
            </g>
          )}
          
          <title>Double-click to edit: {node.name}</title>
        </g>
      );
    }
    
    // Standard mode rendering
    // Show description if: global setting is on OR individual node is expanded
    const nodeShowsDescription = showDescription || expandedDescriptions.has(node.id);
    const nodeHeight = nodeShowsDescription ? NODE_HEIGHT_FULL : NODE_HEIGHT_COMPACT;
    const nodeWidth = calculatedNodeWidth; // Use calculated width with padding
    
    return (
      <g
        key={node.id}
        transform={`translate(${node.x}, ${node.y})`}
        style={{ cursor: reparentMode ? (isBeingReparented ? 'move' : 'pointer') : 'pointer' }}
        onClick={(e) => {
          if (reparentMode && isReparentTarget) {
            e.stopPropagation();
            handleReparentTargetClick(node.id, e.clientX, e.clientY);
          }
        }}
      >
        {/* Reparent mode - dotted border on node being moved */}
        {isBeingReparented && (
          <rect
            x="-8"
            y="-8"
            width={nodeWidth + 16}
            height={nodeHeight + 16}
            rx="14"
            fill="none"
            stroke="#f97316"
            strokeWidth="3"
            strokeDasharray="8,6"
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Focus node outer border */}
        {isFocusNode && !isBeingReparented && (
          <rect
            x="-6"
            y="-6"
            width={nodeWidth + 12}
            height={nodeHeight + 12}
            rx="14"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="4"
            strokeDasharray="none"
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Reparent target highlight */}
        {isReparentTarget && !isBeingReparented && (
          <rect
            x="-4"
            y="-4"
            width={nodeWidth + 8}
            height={nodeHeight + 8}
            rx="12"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Node shadow - skip for unbounded */}
        {!isUnbounded && (
          <rect
            x="3"
            y="3"
            width={nodeWidth}
            height={nodeHeight}
            rx="10"
            fill="rgba(0,0,0,0.1)"
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Node background - when compact, fill entirely with title color */}
        {/* For unbounded style, use minimal styling */}
        {isUnbounded ? (
          /* Unbounded: dotted border, transparent fill */
          <rect
            width={nodeWidth}
            height={nodeHeight}
            rx="10"
            fill={selectedNode?.id === node.id ? 'rgba(102, 126, 234, 0.05)' : 'transparent'}
            stroke={selectedNode?.id === node.id ? colors.text : 'rgba(102, 126, 234, 0.3)'}
            strokeWidth={selectedNode?.id === node.id ? 2 : 1}
            strokeDasharray="6,4"
            style={{ pointerEvents: 'none' }}
          />
        ) : nodeShowsDescription ? (
          <rect
            width={nodeWidth}
            height={nodeHeight}
            rx="10"
            fill={selectedNode?.id === node.id ? '#e0e7ff' : '#ffffff'}
            stroke={colors.stroke}
            strokeWidth={selectedNode?.id === node.id ? 2 : 1}
            style={{ pointerEvents: 'none' }}
          />
        ) : (
          <rect
            width={nodeWidth}
            height={nodeHeight}
            rx="10"
            fill={colors.fill}
            stroke={colors.stroke}
            strokeWidth={selectedNode?.id === node.id ? 2 : 1}
            style={{ pointerEvents: 'none' }}
          />
        )}
        
        {/* Title bar - only needed when showing description (to create the colored header) */}
        {/* Skip for unbounded nodes */}
        {nodeShowsDescription && !isUnbounded && (
          <>
            <rect
              width={nodeWidth}
              height="36"
              rx="10"
              fill={colors.fill}
              style={{ pointerEvents: 'none' }}
            />
            <rect
              y="26"
              width={nodeWidth}
              height="10"
              fill={colors.fill}
              style={{ pointerEvents: 'none' }}
            />
          </>
        )}
        
        {/* Node title - rendered early with pointerEvents: none */}
        {/* Title text color: white for colored headers (standard/light with description), dark for unbounded, white for compact colored nodes */}
        {/* When node padding is enabled, show full text since width is calculated dynamically */}
        <text
          x="12"
          y="24"
          fill={isUnbounded ? colors.text : (nodeShowsDescription ? 'white' : colors.text)}
          fontSize="14"
          fontWeight={isUnbounded ? '400' : '600'}
          style={{ pointerEvents: 'none', fontStyle: isUnbounded ? 'italic' : 'normal' }}
        >
          {(() => {
            const fullName = node.name || node.id || '';
            // When padding is enabled, allow more characters based on node width
            // Calculate max chars based on available width (nodeWidth - 92px for icons/padding)
            // 12px left margin + 80px for icons on right = 92px total reserved
            const availableWidth = nodeWidth - 92;
            const charWidth = 7.5; // Approximate average character width for 14px bold font
            const maxChars = Math.max(10, Math.floor(availableWidth / charWidth));
            return fullName.length > maxChars ? fullName.substring(0, maxChars) + '...' : fullName;
          })()}
        </text>
        
        {/* URL icon - top right corner, opens URL in new tab */}
        {hasUrl && !isUnbounded && (
          <g
            transform={`translate(${nodeWidth - 28}, 8)`}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(node.url, '_blank', 'noopener,noreferrer');
            }}
          >
            <circle cx="10" cy="10" r="12" fill="rgba(255,255,255,0.95)" />
            <path
              d="M7 13L13 7M13 7H9M13 7V11"
              stroke={colors.fill}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <title>Open URL: {node.url}</title>
          </g>
        )}
        
        {/* Collapsed indicator */}
        {node.isCollapsed && node.childCount > 0 && (
          <text
            x="12"
            y="24"
            fill="rgba(255,255,255,0.7)"
            fontSize="10"
            style={{ pointerEvents: 'none' }}
            textAnchor="end"
            dx={nodeWidth - 45}
          >
            +{node.childCount}
          </text>
        )}
        
        {/* Content preview - only show if this node's description is expanded */}
        {nodeShowsDescription && (
          <foreignObject x="10" y="42" width={nodeWidth - 20} height="50">
            <div
              style={{
                fontSize: '12px',
                color: '#64748b',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                pointerEvents: 'none',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
              }}
            >
              {node.description || getPreview(node.body) || <em style={{ color: '#cbd5e1' }}>No content</em>}
            </div>
          </foreignObject>
        )}
        
        {/* Invisible clickable overlay - captures clicks and drag for the whole node */}
        {/* MUST be rendered BEFORE interactive buttons so buttons appear on top */}
        <rect
          width={nodeWidth}
          height={nodeHeight}
          rx="10"
          fill="transparent"
          onClick={(e) => {
            // Handle reparent mode click first
            if (reparentMode && isReparentTarget) {
              e.stopPropagation();
              handleReparentTargetClick(node.id, e.clientX, e.clientY);
              return;
            }
            // Only handle click if not actively dragging (holdProgress check removed - it blocks normal clicks)
            if (!dragState.isDragging) {
              handleNodeClick(e, node);
            }
            // Reset drag state on click
            handleNodeDragCancel();
          }}
          onDoubleClick={(e) => handleNodeDoubleClickWrapper(e, node)}
          onMouseDown={(e) => {
            e.stopPropagation(); // Prevent pan from starting
            if (e.button === 2) {
              e.preventDefault();
              handleNodeDragStart(node.id, e.clientX, e.clientY, true);
            } else if (e.button === 0) {
              handleNodeDragStart(node.id, e.clientX, e.clientY, false);
            }
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
            if (dragState.isDragging && dragState.dropTargetId) {
              handleNodeDrop();
            }
            // Don't cancel here - let onClick handle it
          }}
          onMouseMove={(e) => {
            if (dragState.isDragging) {
              handleNodeDragMove(e.clientX, e.clientY);
            }
          }}
          onMouseEnter={() => {
            if (dragState.isDragging && dragState.draggedNodeId !== node.id) {
              handleSetDropTarget(node.id);
            }
          }}
          onMouseLeave={() => {
            handleClearDropTarget();
          }}
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: dragState.isDragging ? 'grabbing' : 'pointer' }}
        />
        
        {/* Drop target highlight */}
        {dragState.dropTargetId === node.id && (
          <rect
            x="-8"
            y="-8"
            width={nodeWidth + 16}
            height={nodeHeight + 16}
            rx="16"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="4"
            style={{ pointerEvents: 'none', animation: 'pulse 0.5s ease-in-out infinite' }}
          />
        )}
        
        {/* Hold progress indicator */}
        {dragState.draggedNodeId === node.id && dragState.holdProgress > 0 && dragState.holdProgress < 100 && (
          <g transform={`translate(${nodeWidth / 2}, ${nodeHeight / 2})`}>
            <circle r="20" fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }} />
            <circle
              r="20"
              fill="none"
              stroke="#f97316"
              strokeWidth="4"
              strokeDasharray={`${(dragState.holdProgress / 100) * 125.66} 125.66`}
              transform="rotate(-90)"
              style={{ pointerEvents: 'none' }}
            />
          </g>
        )}
        
        {/* Description toggle button (chevron) - AFTER clickable overlay so it's on top */}
        {/* Position: LEFT of URL icon if present, accounting for collapse button too */}
        <g
          onClick={(e) => {
            e.stopPropagation();
            toggleNodeDescription(node.id);
          }}
          style={{ cursor: 'pointer' }}
        >
          {/* Calculate x position: 
              - If node has URL icon (at NODE_WIDTH-28): move left by 32px
              - If node also has children (collapse button): move left by another 28px
          */}
          <rect
            x={hasUrl ? (node.hasChildren ? nodeWidth - 90 : nodeWidth - 62) : (node.hasChildren ? nodeWidth - 56 : nodeWidth - 30)}
            y="8"
            width="22"
            height="22"
            rx="5"
            fill="rgba(255,255,255,0.35)"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="1"
          />
          {/* Chevron - points UP when showing description (can collapse), DOWN when hidden (can expand) */}
          {nodeShowsDescription ? (
            <path 
              d={`M ${hasUrl ? (node.hasChildren ? nodeWidth - 85 : nodeWidth - 57) : (node.hasChildren ? nodeWidth - 51 : nodeWidth - 25)} 21 l 5 -5 l 5 5`}
              stroke="white" 
              strokeWidth="2.5" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <path 
              d={`M ${hasUrl ? (node.hasChildren ? nodeWidth - 85 : nodeWidth - 57) : (node.hasChildren ? nodeWidth - 51 : nodeWidth - 25)} 14 l 5 5 l 5 -5`}
              stroke="white" 
              strokeWidth="2.5" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          <title>{nodeShowsDescription ? 'Hide description' : 'Show description'}</title>
        </g>
        
        {/* Collapse/Expand button (minus/plus) - positioned LEFT of URL icon */}
        {node.hasChildren && (
          <g
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse(node.id);
            }}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={hasUrl ? nodeWidth - 62 : nodeWidth - 30}
              y="8"
              width="22"
              height="22"
              rx="5"
              fill="rgba(255,255,255,0.35)"
              stroke="rgba(255,255,255,0.5)"
              strokeWidth="1"
            />
            {node.isCollapsed ? (
              /* Plus sign when collapsed - click to expand */
              <>
                <line x1={hasUrl ? nodeWidth - 57 : nodeWidth - 25} y1="19" x2={hasUrl ? nodeWidth - 47 : nodeWidth - 15} y2="19" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                <line x1={hasUrl ? nodeWidth - 52 : nodeWidth - 20} y1="14" x2={hasUrl ? nodeWidth - 52 : nodeWidth - 20} y2="24" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              </>
            ) : (
              /* Minus sign when expanded - click to collapse */
              <line x1={hasUrl ? nodeWidth - 57 : nodeWidth - 25} y1="19" x2={hasUrl ? nodeWidth - 47 : nodeWidth - 15} y2="19" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            )}
            <title>{node.isCollapsed ? `Expand (${node.childCount} hidden)` : 'Collapse children'}</title>
          </g>
        )}
        
        {/* Add Child Triangle Button - shown when node is focused (AFTER clickable overlay so it's on top) */}
        {focusNodeId === node.id && (
          <g
            transform={`translate(${nodeWidth + 6}, ${nodeHeight / 2})`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Direct click handler as fallback
              if (!showAddChildMenu) {
                handleCreateChildNode(node.id);
              }
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleAddChildPressStart(node.id, e.clientX, e.clientY);
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              handleAddChildPressEnd(node.id);
            }}
            onMouseLeave={handleAddChildPressCancel}
            onTouchStart={(e) => {
              e.stopPropagation();
              const touch = e.touches[0];
              handleAddChildPressStart(node.id, touch.clientX, touch.clientY);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              handleAddChildPressEnd(node.id);
            }}
            style={{ cursor: 'pointer' }}
          >
            {/* Triangle pointing right - LEFT edge aligned with outline's RIGHT edge */}
            <polygon
              points="0,-16 24,0 0,16"
              fill="#f59e0b"
              stroke="#d97706"
              strokeWidth="1"
            />
            {/* Plus sign - centered in triangle */}
            <line x1="6" y1="0" x2="16" y2="0" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="11" y1="-5" x2="11" y2="5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            <title>Click to add child node (Hold for more options)</title>
          </g>
        )}
        
        <title>Double-click to edit: {node.name}</title>
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <PageHeader lightText={true} />
      
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                Visual Story Editor (Beta)
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Visual Story Editor</h1>
          <p className="text-gray-600">Create and edit your interactive stories with a visual node-based interface.</p>
        </div>
        
        {/* Hidden file input for import - always available */}
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileChange} 
          accept=".jsont,.json" 
          style={{ display: 'none' }} 
        />
        
        {!showCanvas ? (
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
            <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">Get Started</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Import existing Twee code or start with a blank story.</p>
              </div>
              
              {/* Continue Previous Work - shown when there's saved data */}
              {preloadSource && tweeInput && (
                <div className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-2">
                      <span className="text-xl sm:text-2xl">📂</span>
                      <div>
                        <h3 className="text-sm sm:text-base font-semibold text-emerald-800">Continue Previous Work</h3>
                        <p className="text-xs sm:text-sm text-emerald-700">
                          Found saved Twee from <strong>{preloadSource}</strong>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 self-end sm:self-auto">
                      <Button 
                        onClick={handleImportTwee}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <FileText className="w-4 h-4 mr-1" />Continue
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setPreloadSource(null);
                          setTweeInput('');
                        }}
                        className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="twee-input" className="text-base sm:text-lg font-semibold mb-2 block">Import Twee Code</Label>
                <Textarea
                  id="twee-input"
                  value={tweeInput}
                  onChange={(e) => setTweeInput(e.target.value)}
                  placeholder=":: StoryTitle\nMy Interactive Story\n\n:: Start\nYou wake up in a mysterious forest.\n\n[[Explore the forest|Forest]]\n[[Look for a path|Path]]"
                  rows={8}
                  className="font-mono text-xs sm:text-sm"
                />
                <div className="flex gap-3 mt-3 sm:mt-4">
                  <Button onClick={handleImportTwee} className="flex-1 text-sm sm:text-base">
                    <FileText className="w-4 h-4 mr-2" />Import and Generate Map
                  </Button>
                </div>
                
                {/* Import from file */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Or import from a saved project file:</span>
                    <Button onClick={handleImportProject} variant="outline" size="sm">
                      <Upload className="w-4 h-4 mr-2" />Import .jsont File
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">OR</span>
                </div>
              </div>
              
              <div className="text-center">
                <Button onClick={handleStartEmpty} variant="outline" size="lg" className="w-full sm:w-auto">
                  <Plus className="w-5 h-5 mr-2" />Start with Empty Story
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-3 sm:p-4 mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs sm:text-sm flex-wrap">
                  <span className="text-gray-600">{graph.nodes.length} passage{graph.nodes.length !== 1 ? 's' : ''}</span>
                  <span className="text-gray-300">|</span>
                  <span className="text-gray-600">{edges.length} connection{edges.length !== 1 ? 's' : ''}</span>
                  {collapsedNodes.size > 0 && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-amber-600">{collapsedNodes.size} collapsed</span>
                    </>
                  )}
                  {localStorage.getItem('story_planner_story_id') && (
                    <>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => navigate('/visual-story-planner')}
                        className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                      >
                        <ArrowLeft className="w-3 h-3" />Back to Planner
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Canvas */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6">
              <div className="mb-4 pb-3 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">{graph.storyTitle || 'Interactive Story'}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {graph.nodes.length} passage{graph.nodes.length !== 1 ? 's' : ''} • {edges.length} connection{edges.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              {/* Action Buttons - moved inside story box */}
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-start mb-4 pb-3 border-b border-gray-200">
                <Button onClick={handleSaveProject} variant="outline" size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                  <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Save</span>
                </Button>
                <Button onClick={handleImportProject} variant="outline" size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3">
                  <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Import</span>
                </Button>
                <Button disabled size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 bg-gray-300 text-gray-500 cursor-not-allowed" title="No-JS export temporarily disabled">
                  <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">No-JS</span>
                </Button>
                
                {/* Mindmap Exports Group with Label */}
                <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-200 rounded-lg border border-slate-300" title="Mindmap Exports">
                  <span className="text-xs font-semibold text-slate-600 hidden sm:block">Mindmaps:</span>
                  <Button onClick={handleExportWpJs} size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 bg-emerald-500 hover:bg-emerald-600 text-white" title="Copy WordPress Mindmap HTML">
                    <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">WordPress</span>
                  </Button>
                  <Button onClick={handleExportPathNav} size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 bg-cyan-500 hover:bg-cyan-600 text-white" title="Copy PathNav sitemap for WordPress headers">
                    <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">WP Nav</span>
                  </Button>
                </div>
                
                {/* Story Export */}
                <Button onClick={handleExportWpStory} size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 bg-purple-500 hover:bg-purple-600 text-white" title="Copy Interactive Story HTML for WordPress">
                  <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">WP Story</span>
                </Button>
                
                <Button onClick={() => {
                  // Build story config from current Visual Editor settings
                  const storyConfig = {
                    layoutDirection: layoutDirection || 'right',
                    focusNodeViewEnabled: focusNodeViewEnabled || false,
                    focusLayerDepth: focusLayerDepth || 2,
                    dynamicViewSize: dynamicViewSize || 'standard',
                    showDescription: showDescription || false,
                    showBackLinks: showBackLinks || false,
                    // PathNav filter settings
                    hideH1Nodes: hideH1Nodes || false,
                    hideH2Nodes: hideH2Nodes || false,
                  };
                  
                  const currentTwee = graphToTwee(graph);
                  localStorage.setItem('visual_editor_twee', currentTwee);
                  localStorage.setItem('visual_editor_graph', JSON.stringify(graph));
                  localStorage.setItem('visual_editor_config', JSON.stringify(storyConfig));
                  navigate('/compiler');
                }} size="sm" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 bg-emerald-500 hover:bg-emerald-600 text-white">
                  <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Compiler</span>
                </Button>
              </div>
              
              {/* Zoom Controls */}
              <div className="flex items-center justify-between mb-3 px-2 py-2 bg-slate-100 rounded-lg">
                <div className="text-xs text-slate-600 hidden sm:block">Drag to pan • Hold node to reparent • Ctrl+Scroll to zoom • Double-click to edit</div>
                <div className="text-xs text-slate-600 sm:hidden">Pinch to zoom • Tap [-] to collapse</div>
                
                <div className="flex items-center gap-1">
                  <button onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed" title="Zoom Out">
                    <ZoomOut className="w-4 h-4 text-slate-700" />
                  </button>
                  
                  <div className="relative">
                    <button onClick={() => setShowZoomDropdown(!showZoomDropdown)} className="flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 min-w-[70px] justify-center text-sm font-medium text-slate-700">
                      {Math.round(zoom)}%<ChevronDown className="w-3 h-3" />
                    </button>
                    
                    {showZoomDropdown && (
                      <div className="absolute top-full mt-1 right-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[100px] max-h-60 overflow-y-auto">
                        {ZOOM_PRESETS.map(preset => (
                          <button key={preset} onClick={() => handleZoomSelect(preset)} className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-100 ${Math.round(zoom) === preset ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700'}`}>
                            {preset}%
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <button onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed" title="Zoom In">
                    <ZoomIn className="w-4 h-4 text-slate-700" />
                  </button>
                  
                  <button onClick={handleResetView} className="p-1.5 rounded hover:bg-slate-200 ml-2" title="Reset View">
                    <Maximize2 className="w-4 h-4 text-slate-700" />
                  </button>
                  
                  {/* Undo Button */}
                  <button 
                    onClick={handleUndo} 
                    disabled={undoHistory.length === 0}
                    className="p-1.5 rounded hover:bg-slate-200 ml-2 disabled:opacity-40 disabled:cursor-not-allowed" 
                    title={`Undo (Ctrl+Z) - ${undoHistory.length} steps available`}
                  >
                    <Undo2 className="w-4 h-4 text-slate-700" />
                  </button>
                  
                  {collapsedNodes.size > 0 && (
                    <button onClick={() => setCollapsedNodes(new Set())} className="p-1.5 rounded hover:bg-slate-200 ml-1 text-amber-600" title="Expand All">
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Layout Options */}
              <div className="flex flex-wrap items-center gap-3 mb-3 px-2 py-2 bg-indigo-50 rounded-lg text-sm">
                {/* Direction Select */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 font-medium">Direction:</span>
                  <select 
                    value={layoutDirection}
                    onChange={(e) => {
                      const newDir = e.target.value;
                      setLayoutDirection(newDir);
                      // Reset alignment when switching between horizontal/vertical
                      if ((newDir === 'up' || newDir === 'down') && (nodeAlignment === 'above' || nodeAlignment === 'below')) {
                        setNodeAlignment('center');
                      }
                    }}
                    className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 min-w-[90px]"
                  >
                    <option value="right">→ Right</option>
                    <option value="left">← Left</option>
                    <option value="down">↓ Down</option>
                    <option value="up">↑ Up</option>
                  </select>
                </div>

                {/* Alignment Select - Context sensitive */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 font-medium">Align:</span>
                  <select 
                    value={nodeAlignment}
                    onChange={(e) => setNodeAlignment(e.target.value)}
                    className="px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 min-w-[100px]"
                  >
                    <option value="center">Center</option>
                    {(layoutDirection === 'up' || layoutDirection === 'down') ? (
                      <>
                        <option value="below">Right Only</option>
                        <option value="above">Left Only</option>
                      </>
                    ) : (
                      <>
                        <option value="below">Below Only</option>
                        <option value="above">Above Only</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Show Description Checkbox */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={showDescription} 
                      onChange={(e) => setShowDescription(e.target.checked)}
                      data-testid="visual-editor-checkbox-toggle"
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-slate-600">Show Description</span>
                  </label>
                  {showDescription && (
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        value={descriptionCharLimit}
                        onChange={(e) => setDescriptionCharLimit(Math.max(20, Math.min(500, parseInt(e.target.value) || 80)))}
                        className="w-16 px-1.5 py-0.5 rounded border border-slate-300 text-center text-slate-700"
                        min="20"
                        max="500"
                      />
                      <span className="text-slate-500 text-xs">chars</span>
                    </div>
                  )}
                </div>

                {/* Show Back Links Checkbox */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={showBackLinks} 
                      onChange={(e) => setShowBackLinks(e.target.checked)}
                      data-testid="visual-editor-show-back-links-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-slate-600">Show Back Links</span>
                  </label>
                </div>

                {/* Hide H1/H2 Nodes (PathNav) */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={hideH1Nodes} 
                      onChange={(e) => setHideH1Nodes(e.target.checked)}
                      data-testid="visual-editor-hide-h1-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-slate-600 text-sm">Hide H1</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={hideH2Nodes} 
                      onChange={(e) => setHideH2Nodes(e.target.checked)}
                      data-testid="visual-editor-hide-h2-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-slate-600 text-sm">Hide H2</span>
                  </label>
                  <button
                    onClick={() => setShowNodeFormatEditor(true)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm hover:from-indigo-600 hover:to-purple-600 transition-all"
                    title="Customize node colors"
                  >
                    <Palette className="w-3.5 h-3.5" />
                    <span>Format</span>
                  </button>
                </div>

                {/* Focus Node View Checkbox + Layer Dropdown */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={focusNodeViewEnabled} 
                      onChange={(e) => setFocusNodeViewEnabled(e.target.checked)}
                      data-testid="visual-editor-focus-node-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-slate-600">Focus Node View</span>
                  </label>
                  {focusNodeViewEnabled && (
                    <>
                      <select
                        value={focusLayerDepth}
                        onChange={(e) => setFocusLayerDepth(parseInt(e.target.value))}
                        className="px-2 py-1 rounded border border-slate-300 text-slate-700 text-sm"
                      >
                        <option value={1}>Show 1 Layer</option>
                        <option value={2}>Show 2 Layers</option>
                        <option value={3}>Show 3 Layers</option>
                      </select>
                      <label className="flex items-center gap-1 cursor-pointer ml-2">
                        <input 
                          type="checkbox" 
                          checked={focusTravelCenter} 
                          onChange={(e) => setFocusTravelCenter(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-slate-500 text-xs">Center</span>
                      </label>
                    </>
                  )}
                  {focusNodeViewEnabled && !focusNodeId && (
                    <span className="text-amber-600 text-xs italic">Click a node to focus</span>
                  )}
                </div>

                {/* Dynamic Node View Checkbox + Size Dropdown */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={dynamicViewEnabled} 
                      onChange={(e) => setDynamicViewEnabled(e.target.checked)}
                      data-testid="visual-editor-dynamic-view-checkbox"
                      disabled={pathNavSitemapMode}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-50"
                    />
                    <span className={`text-slate-600 ${pathNavSitemapMode ? 'opacity-50' : ''}`}>Dynamic View</span>
                  </label>
                  {dynamicViewEnabled && (
                    <select
                      value={dynamicViewSize}
                      onChange={(e) => setDynamicViewSize(e.target.value)}
                      disabled={pathNavSitemapMode}
                      className="px-2 py-1 rounded border border-slate-300 text-slate-700 text-sm disabled:opacity-50"
                    >
                      <option value="standard">Standard</option>
                      <option value="compact">Compact</option>
                    </select>
                  )}
                  {dynamicViewEnabled && !pathNavSitemapMode && (
                    <span className="text-emerald-600 text-xs italic ml-1">StoryNav mode</span>
                  )}
                </div>
                
                {/* PathNav Sitemap Mode */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={pathNavSitemapMode} 
                      onChange={(e) => handlePathNavModeToggle(e.target.checked)}
                      data-testid="visual-editor-pathnav-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-slate-600">PathNav Sitemap</span>
                  </label>
                  {pathNavSitemapMode && (
                    <>
                      <span className="text-xs text-slate-500">Focus:</span>
                      <input 
                        type="color" 
                        value={pathNavFocusColor}
                        onChange={(e) => setPathNavFocusColor(e.target.value)}
                        className="w-6 h-6 rounded cursor-pointer border border-slate-300"
                        title="Focus border color"
                      />
                      <label className="flex items-center gap-1 ml-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={pathNavStartMinimized}
                          onChange={(e) => setPathNavStartMinimized(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="text-xs text-slate-500">Start minimized</span>
                      </label>
                    </>
                  )}
                </div>
                
                {/* Node Width Padding Control */}
                <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={nodeWidthPaddingEnabled} 
                      onChange={(e) => setNodeWidthPaddingEnabled(e.target.checked)}
                      data-testid="visual-editor-node-padding-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    />
                    <span className="text-slate-600 text-sm">Node Padding</span>
                  </label>
                  {nodeWidthPaddingEnabled && (
                    <select
                      value={nodeWidthPaddingPercent}
                      onChange={(e) => setNodeWidthPaddingPercent(e.target.value)}
                      className="px-2 py-1 rounded border border-slate-300 text-slate-700 text-xs"
                    >
                      <option value="0">0% (Tight)</option>
                      <option value="10">10%</option>
                      <option value="30">30%</option>
                      <option value="50">50%</option>
                      <option value="80">80%</option>
                      <option value="100">100% (Full)</option>
                    </select>
                  )}
                </div>
              </div>
              
              {/* Canvas Container */}
              <div
                ref={containerRef}
                className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-slate-50"
                style={{ height: '65vh', minHeight: '400px', cursor: dragState.isDragging ? 'grabbing' : (isPanning ? 'grabbing' : 'grab'), touchAction: 'none' }}
                onMouseDown={handleMouseDown}
                onMouseMove={(e) => {
                  handleMouseMove(e);
                  if (dragState.isDragging) handleNodeDragMove(e.clientX, e.clientY);
                }}
                onMouseUp={(e) => {
                  if (dragState.isDragging) {
                    handleNodeDrop();
                  } else {
                    handleMouseUp(e);
                    handleNodeDragCancel();
                  }
                }}
                onMouseLeave={(e) => {
                  handleMouseUp(e);
                  if (dragState.isDragging) handleNodeDragCancel();
                }}
                onTouchStart={(e) => {
                  handleTouchStart(e);
                  handleCanvasDoubleTap(e);
                }}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
              >
                <div className="canvas-bg absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)', backgroundSize: `${20 * (zoom / 100)}px ${20 * (zoom / 100)}px`, backgroundPosition: `${panOffset.x}px ${panOffset.y}px` }} />
                
                <div ref={canvasRef} className="absolute" style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom / 100})`, transformOrigin: '0 0', width: canvasSize.width, height: canvasSize.height }}>
                  <svg className="absolute top-0 left-0" width={canvasSize.width} height={canvasSize.height} style={{ overflow: 'visible' }}>
                    <defs>
                      {/* Arrow markers - refX=0 so line connects to base of triangle, not tip */}
                      <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                        <polygon points="0 0, 10 3, 0 6" fill="#667eea" />
                      </marker>
                      <marker id="arrowhead-return" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto" markerUnits="strokeWidth">
                        <polygon points="0 0, 10 3, 0 6" fill="#94a3b8" opacity="0.6" />
                      </marker>
                    </defs>
                    {renderEdges()}
                    {getVisibleNodesForFocusView().map(renderNode)}
                  </svg>
                </div>
                
                {isCalculatingLayout && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-500 bg-white/80">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                      <p className="text-lg mb-2">Calculating layout...</p>
                      <p className="text-sm">Please wait while we position your story nodes</p>
                    </div>
                  </div>
                )}
                
                {!isCalculatingLayout && layoutedNodes.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <p className="text-lg mb-2">No passages to display</p>
                      <p className="text-sm">Add passages using the &quot;+Add&quot; button</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-center text-sm text-gray-500">
                <p>Double-click any passage to edit • Click [-/+] on nodes to collapse/expand branches • Add choices like [[Go here|Target]] to create connections</p>
              </div>
            </div>
          </>
        )}
      </div>
      
      <NodeEditModal
        node={editingNode}
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingNode(null); }}
        onSave={handleSaveNode}
        allNodes={graph.nodes}
        onCreateNode={handleCreateNode}
      />
      
      {/* Node Format Editor Modal */}
      <NodeFormatEditor
        isOpen={showNodeFormatEditor}
        onClose={() => setShowNodeFormatEditor(false)}
        nodeFormat={nodeFormat}
        onFormatChange={setNodeFormat}
      />
      
      {/* Add Child Menu Popup - simplified with Move Node option */}
      {showAddChildMenu && (
        <div 
          className="fixed z-50"
          style={{ 
            left: addChildMenuPosition.x, 
            top: addChildMenuPosition.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2 min-w-[200px]">
            <div className="px-3 py-1.5 border-b border-slate-100">
              <span className="text-xs text-slate-500 font-medium">Node Actions</span>
            </div>
            <button
              onClick={() => {
                handleCreateChildNode(addChildParentId);
                setShowAddChildMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
            >
              <Plus className="w-4 h-4 text-orange-500" />
              <span>Add Child Node</span>
            </button>
            <button
              onClick={() => {
                enterReparentMode(addChildParentId);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4 text-blue-500" />
              <span>Move Node...</span>
            </button>
            {/* Delete Node Section */}
            <div className="border-t border-slate-100 mt-1 pt-1">
              {(() => {
                // Calculate children count for this node
                const childIds = new Set();
                const findChildren = (id) => {
                  (graph.edges || []).forEach(edge => {
                    const from = edge.from || edge.source;
                    const to = edge.to || edge.target;
                    if (from === id && !to.toLowerCase().includes('back')) {
                      if (!childIds.has(to)) {
                        childIds.add(to);
                        findChildren(to);
                      }
                    }
                  });
                };
                findChildren(addChildParentId);
                const childCount = childIds.size;
                
                return (
                  <>
                    <button
                      onClick={() => {
                        if (childCount > 0 && !deleteChildrenChecked) {
                          toast({ 
                            title: 'Cannot Delete', 
                            description: `This node has ${childCount} children. Check the box to delete all.`,
                            variant: 'destructive'
                          });
                          return;
                        }
                        handleDeleteNode(addChildParentId, deleteChildrenChecked);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Node</span>
                    </button>
                    {childCount > 0 && (
                      <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={deleteChildrenChecked}
                          onChange={(e) => setDeleteChildrenChecked(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-slate-300"
                        />
                        <span>Delete {childCount} children</span>
                      </label>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="border-t border-slate-100 mt-1 pt-1">
              <button
                onClick={() => {
                  setShowAddChildMenu(false);
                  setDeleteChildrenChecked(false);
                }}
                className="w-full px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
          {/* Click outside to close */}
          <div 
            className="fixed inset-0 -z-10" 
            onClick={() => {
              setShowAddChildMenu(false);
              setDeleteChildrenChecked(false);
            }} 
          />
        </div>
      )}
      
      {/* Reparent Confirmation Popup */}
      {showReparentConfirm && (
        <div 
          className="fixed z-50"
          style={{ 
            left: reparentConfirmPosition.x, 
            top: reparentConfirmPosition.y,
            transform: 'translate(-50%, -50%)'
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              confirmReparent();
            } else if (e.key === 'Escape') {
              setShowReparentConfirm(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-4 min-w-[220px]">
            <div className="text-center mb-4">
              <p className="text-sm font-medium text-slate-700">Move here?</p>
              <p className="text-xs text-slate-500 mt-1">
                Move under &ldquo;{graph.nodes.find(n => n.id === reparentTargetId)?.name}&rdquo;
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                autoFocus
                onClick={confirmReparent}
                className="px-4 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 focus:outline-none flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                OK (Enter)
              </button>
              <button
                onClick={cancelReparentMode}
                className="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm rounded hover:bg-slate-300 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Cancel (Esc)
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-3">Click another node to change target</p>
          </div>
          {/* Clicking outside closes popup but stays in reparent mode - can pick another node */}
          <div 
            className="fixed inset-0 -z-10" 
            onClick={() => setShowReparentConfirm(false)} 
          />
        </div>
      )}
      
      {/* Reparent Mode Banner */}
      {reparentMode && !showReparentConfirm && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-orange-100 border border-orange-300 rounded-lg px-4 py-2 shadow-lg flex items-center gap-3">
          <span className="text-sm text-orange-800 font-medium">
            🔄 Reparent Mode: Click on a target node to move the selected node
          </span>
          <button
            onClick={cancelReparentMode}
            className="px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded hover:bg-orange-300"
          >
            Cancel (Esc)
          </button>
        </div>
      )}
      
      {showZoomDropdown && <div className="fixed inset-0 z-30" onClick={() => setShowZoomDropdown(false)} />}
    </div>
  );
};

export default VisualEditor;
